import type { UiLocale } from './uiMessages';
import { MODERATOR_PAGE_MESSAGES_FR } from './moderatorPagesFr';

/** Moderator page UI strings (merged into UI_MESSAGES). */
export const MODERATOR_PAGE_MESSAGES: Record<UiLocale, Record<string, string>> = {
  en: {
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.delete': 'Delete',
    'common.close': 'Close',
    'common.loading': 'Loading',
    'common.retry': 'Retry',
    'common.all': 'All',
    'common.previous': 'Previous',
    'common.next': 'Next',
    'common.today': 'Today',
    'common.tomorrow': 'Tomorrow',
    'common.yesterday': 'Yesterday',
    'common.subscriber': 'Subscriber',
    'common.template': 'Template',
    'common.status': 'Status',
    'common.actions': 'Actions',
    'common.open': 'Open',
    'common.search': 'Search',
    'common.clearFilters': 'Clear filters',
    'common.never': 'Never',
    'common.justNow': 'Just now',
    'common.minAgo': '{n} min ago',
    'common.hoursAgo': '{n} hour(s) ago',
    'common.daysAgo': '{n} day(s) ago',
    'common.unknownTime': 'unknown time',
    'common.secondsAgo': '{n}s ago',
    'common.minutesAgoShort': '{n}m ago',
    'common.hoursAgoShort': '{n}h ago',
    'common.daysAgoShort': '{n}d ago',
    'common.articles': '{count} articles',
    'common.articlesOf': '{shown} of {total} articles',
    'common.articlesToday': '{count} articles today',
    'common.optional': '(optional)',
    'common.reject': 'Reject',
    'common.send': 'Send',
    'common.remove': 'Remove',
    'common.saved': 'Saved',
    'common.dismiss': 'Dismiss',
    'common.off': 'Off',
    'common.paused': 'Paused',
    'common.success': 'Success',
    'common.failed': 'Failed',
    'common.uncategorized': 'Uncategorized',
    'common.articleNumber': 'Article #{id}',
    'common.create': 'Create',
    'common.edit': 'Edit',
    'common.reset': 'Reset',
    'common.email': 'Email',
    'common.username': 'Username',
    'common.language': 'Language',
    'common.password': 'Password',
    'common.saving': 'Saving…',
    'common.creating': 'Creating…',
    'common.adding': 'Adding…',
    'common.removing': 'Removing…',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.system': 'System',
    'common.custom': 'Custom',
    'common.default': 'Default',
    'common.topics': 'Topics',
    'common.buzzwords': 'Buzzwords',
    'common.keywords': 'Keywords',
    'common.preview': 'Preview',
    'common.duplicate': 'Duplicate',
    'common.description': 'Description',
    'common.frequency': 'Frequency',
    'common.timezone': 'Timezone',
    'common.profile': 'Profile',
    'common.memberSince': 'Member since',
    'common.daily': 'Daily',
    'common.topic': 'Topic',
    'common.schedule': 'Schedule',
    'common.regenerate': 'Regenerate',
    'common.showingRange': 'Showing {from} to {to} of {total}',

    'emailTemplates.title': 'Email Templates',
    'emailTemplates.subtitle': 'Choose, preview, customize and assign email templates to subscribers.',
    'emailTemplates.importTemplate': 'Import Template',
    'emailTemplates.newCustomTemplate': 'New Custom Template',
    'emailTemplates.toastImportSoon': 'Import feature coming soon.',
    'emailTemplates.loadErrorTitle': 'Could not load templates from the API',
    'emailTemplates.toastLoadTemplates': 'Could not load email templates: {msg}',
    'emailTemplates.toastLoadSubscribers': 'Could not load subscribers: {msg}',
    'emailTemplates.statTotal': 'Total Templates',
    'emailTemplates.statTotalSub': 'All email templates',
    'emailTemplates.statSystem': 'System Templates',
    'emailTemplates.statSystemSub': 'Provided by system',
    'emailTemplates.statCustom': 'Custom Templates',
    'emailTemplates.statCustomSub': 'Created by you',
    'emailTemplates.statAssigned': 'Assigned Subscribers',
    'emailTemplates.statAssignedSub': 'With template assigned',
    'emailTemplates.libraryTitle': 'Template Library',
    'emailTemplates.searchPlaceholder': 'Search templates…',
    'emailTemplates.tab.all': 'All',
    'emailTemplates.tab.system': 'System',
    'emailTemplates.tab.custom': 'Custom',
    'emailTemplates.tab.shared': 'Shared',
    'emailTemplates.badge.default': 'Default',
    'emailTemplates.badge.shared': '@Shared',
    'emailTemplates.usedByOne': 'Used by {count} subscriber',
    'emailTemplates.usedByMany': 'Used by {count} subscribers',
    'emailTemplates.emptyTemplates': 'No templates found.',
    'emailTemplates.showingTemplates': 'Showing 1 to {shown} of {total} templates',
    'emailTemplates.detailsTitle': 'Template Details & Preview',
    'emailTemplates.usedByLabel': 'Used by',
    'emailTemplates.subscriberOne': '{count} subscriber',
    'emailTemplates.subscriberMany': '{count} subscribers',
    'emailTemplates.lastUpdated': 'Last updated',
    'emailTemplates.createdBy': 'Created by',
    'emailTemplates.createdBySystem': 'System',
    'emailTemplates.createdByYou': 'You',
    'emailTemplates.actionsTitle': 'Actions',
    'emailTemplates.setAsDefault': 'Set as Default',
    'emailTemplates.sendTestEmail': 'Send Test Email',
    'emailTemplates.toastDuplicateSoon': 'Duplicate feature coming soon.',
    'emailTemplates.toastTestEmailSoon': 'Test email feature coming soon.',
    'emailTemplates.selectToViewDetails': 'Select a template to view details',
    'emailTemplates.previewTitle': 'Preview',
    'emailTemplates.desktop': 'Desktop',
    'emailTemplates.mobile': 'Mobile',
    'emailTemplates.fromLabel': 'From: hawk@digest.io',
    'emailTemplates.viewInBrowser': 'View in browser',
    'emailTemplates.previewDigestLine': '— Your personalized digest content',
    'emailTemplates.previewPoweredLine': '— Powered by Hawk AI',
    'emailTemplates.moreArticles': '+ more articles personalized for each subscriber…',
    'emailTemplates.poweredBy': 'Powered by Hawk AI',
    'emailTemplates.selectToPreview': 'Select a template to preview',
    'emailTemplates.assignmentsTitle': 'Subscriber Template Assignments',
    'emailTemplates.assignmentsSubtitle': 'Assign templates to subscribers. Subscribers without assignment use the default template.',
    'emailTemplates.searchSubscriber': 'Search subscriber…',
    'emailTemplates.filterTemplate': 'Template',
    'emailTemplates.filterStatus': 'Status',
    'emailTemplates.statusSaved': 'Saved',
    'emailTemplates.statusUnsaved': 'Unsaved',
    'emailTemplates.bulkAssign': 'Bulk Assign',
    'emailTemplates.toastBulkAssignSoon': 'Bulk assign coming soon.',
    'emailTemplates.colAssignedTemplate': 'Assigned Template',
    'emailTemplates.colTemplateType': 'Template Type',
    'emailTemplates.colLastUpdated': 'Last Updated',
    'emailTemplates.changeTemplate': 'Change Template',
    'emailTemplates.resetToDefault': 'Reset to Default',
    'emailTemplates.emptySubscribers': 'No subscribers found.',
    'emailTemplates.showingSubscribers': 'Showing 1 to {shown} of {total} subscribers',
    'emailTemplates.dialogNewTitle': 'New Custom Template',
    'emailTemplates.dialogEditTitle': 'Edit Template',
    'emailTemplates.saveChanges': 'Save changes',
    'emailTemplates.deleteTitle': 'Delete template?',
    'emailTemplates.deleteDesc': 'Delete {name}? Subscribers assigned to this template will revert to the default. This cannot be undone.',
    'emailTemplates.changeAssignmentTitle': 'Change Template Assignment',
    'emailTemplates.changeAssignmentDesc': 'Assign a template to {email}',
    'emailTemplates.useDefault': 'Use default',
    'emailTemplates.defaultSuffix': '(default)',
    'emailTemplates.toastCreated': 'Template created.',
    'emailTemplates.toastUpdated': 'Template updated.',
    'emailTemplates.toastDefaultUpdated': 'Default template updated.',
    'emailTemplates.toastDeleted': 'Template deleted.',
    'emailTemplates.toastResetAssignment': 'Reset to default template.',
    'emailTemplates.toastAssignmentUpdated': 'Template assignment updated.',
    'emailTemplates.formName': 'Template name',
    'emailTemplates.formNamePlaceholder': 'e.g. My Custom Digest',
    'emailTemplates.formDescriptionPlaceholder': 'Short description of this layout',
    'emailTemplates.formTag': 'Tag',
    'emailTemplates.formTagPlaceholder': 'e.g. minimal, tech, finance',
    'emailTemplates.formAccent': 'Accent colour',
    'emailTemplates.accent.blue': 'Blue',
    'emailTemplates.accent.amber': 'Amber',
    'emailTemplates.accent.cyan': 'Cyan',
    'emailTemplates.accent.rose': 'Rose',

    'subscribers.title': 'Subscribers',
    'subscribers.subtitle': 'Manage recipients, delivery schedules, and newsletter preferences.',
    'subscribers.importCsv': 'Import CSV',
    'subscribers.toastImportCsvSoon': 'CSV import coming soon.',
    'subscribers.addSubscriber': 'Add Subscriber',
    'subscribers.statTotal': 'Total Subscribers',
    'subscribers.statTotalSub': 'All registered recipients',
    'subscribers.statActive': 'Active Subscribers',
    'subscribers.statActiveSub': 'Ready to receive newsletters',
    'subscribers.statScheduled': 'Scheduled Today',
    'subscribers.statScheduledSub': 'Next send at 06:00 CET',
    'subscribers.statIssues': 'Delivery Issues',
    'subscribers.statIssuesSub': 'Needs your attention',
    'subscribers.tableTitle': 'Subscribers',
    'subscribers.loadingDirectory': 'Loading directory…',
    'subscribers.shownOfTotal': '{filtered} of {total} shown',
    'subscribers.searchPlaceholder': 'Search email or username…',
    'subscribers.filterAllLanguages': 'All languages',
    'subscribers.filterAllTopics': 'All topics',
    'subscribers.filterScheduled': 'Scheduled',
    'subscribers.filterNotScheduled': 'Not scheduled',
    'subscribers.loading': 'Loading subscribers…',
    'subscribers.colPreferences': 'Preferences',
    'subscribers.colDelivery': 'Delivery',
    'subscribers.colLastSent': 'Last Sent',
    'subscribers.topicsLabel': 'Topics:',
    'subscribers.dailyAt': 'Daily at {time} {tz}',
    'subscribers.emailDigest': 'Email digest',
    'subscribers.lastDeliverySuccess': '• Last delivery: Success',
    'subscribers.notScheduled': 'Not scheduled',
    'subscribers.lastDeliveryNa': '• Last delivery: N/A',
    'subscribers.importTopicsTitle': 'Import topics & buzzwords (.xlsx/.csv)',
    'subscribers.importTopicsAria': 'Import topics and buzzwords',
    'subscribers.emptyNone': 'No subscribers yet. Add one to get started.',
    'subscribers.emptyFilters': 'No subscribers match your filters.',
    'subscribers.showingSubscribers': 'Showing {from} to {to} of {total} subscribers',
    'subscribers.perPage': '{n} / page',
    'subscribers.sidebarDetails': 'Subscriber Details',
    'subscribers.memberSince': 'Member since',
    'subscribers.topicsSectionTitle': 'Topics, Buzzwords & Keywords',
    'subscribers.topicsSectionDesc': 'Buzzwords for newsletter content. Keywords match articles in the scraper pool.',
    'subscribers.fullEditor': 'Full editor',
    'subscribers.noTopicsHint': 'No topics yet. Add a topic below to assign buzzwords and keywords.',
    'subscribers.noBuzzwordsYet': 'No buzzwords yet',
    'subscribers.noKeywordsYet': 'No keywords yet',
    'subscribers.addBuzzwordPlaceholder': 'Add buzzword…',
    'subscribers.addKeywordPlaceholder': 'Add keyword…',
    'subscribers.addTopic': 'Add topic',
    'subscribers.topicNamePlaceholder': 'Topic name…',
    'subscribers.removeBuzzwordAria': 'Remove buzzword {word}',
    'subscribers.removeKeywordAria': 'Remove keyword {word}',
    'subscribers.deliverySettings': 'Delivery Settings',
    'subscribers.sendTime': 'Send Time',
    'subscribers.emailTemplate': 'Email Template',
    'subscribers.defaultDigest': 'Default Digest',
    'subscribers.deliveryHistory': 'Delivery History',
    'subscribers.lastSent': 'Last Sent',
    'subscribers.lastStatus': 'Last Status',
    'subscribers.lastError': 'Last Error',
    'subscribers.sendTestEmail': 'Send Test Email',
    'subscribers.toastTestEmailSoon': 'Test email feature coming soon.',
    'subscribers.pauseSubscriber': 'Pause Subscriber',
    'subscribers.toastPauseSoon': 'Pause feature coming soon.',
    'subscribers.deleteSubscriber': 'Delete Subscriber',
    'subscribers.editTitle': 'Edit subscriber',
    'subscribers.emailLanguage': 'Email language',
    'subscribers.newPasswordOptional': 'New password (optional)',
    'subscribers.passwordKeepCurrent': 'Leave blank to keep current',
    'subscribers.passwordMinHint': 'Minimum 8 characters when set.',
    'subscribers.deleteTitle': 'Remove subscriber',
    'subscribers.deleteDesc': 'Are you sure you want to remove {email}? This cannot be undone.',
    'subscribers.addTitle': 'Add subscriber',
    'subscribers.emailPlaceholder': 'subscriber@example.com',
    'subscribers.usernamePlaceholder': 'login name',
    'subscribers.passwordPlaceholder': 'At least 8 characters',
    'subscribers.toastImportNothing': 'Nothing new to import — topics and buzzwords already up to date.',
    'subscribers.toastImportSuccess': 'Imported {created} new topic(s), merged {merged}, added {buzz} buzzword(s).',
    'subscribers.toastBuzzwordAdded': 'Buzzword added.',
    'subscribers.toastBuzzwordRemoved': 'Buzzword removed.',
    'subscribers.toastKeywordAdded': 'Keyword added.',
    'subscribers.toastKeywordRemoved': 'Keyword removed.',
    'subscribers.toastTopicExists': 'Topic already exists.',
    'subscribers.toastTopicAdded': 'Topic added.',
    'subscribers.errEmailUsernameRequired': 'Email and username are required.',
    'subscribers.errPasswordMin': 'New password must be at least 8 characters.',
    'subscribers.toastUpdated': 'Subscriber updated.',
    'subscribers.toastRemoved': 'Subscriber removed.',
    'subscribers.errAllFieldsRequired': 'Email, username, and password are required.',
    'subscribers.errPasswordMinCreate': 'Password must be at least 8 characters.',
    'subscribers.toastAdded': 'Subscriber added.',

    'subscriberDetail.notFound': 'Subscriber not found.',
    'subscriberDetail.backToSubscribers': 'Back to Subscribers',
    'subscriberDetail.regenerate': 'Regenerate',
    'subscriberDetail.sendNow': 'Send now',
    'subscriberDetail.saveChanges': 'Save changes',
    'subscriberDetail.allChangesSaved': 'All changes saved',
    'subscriberDetail.dailySendTime': 'Daily Send Time',
    'subscriberDetail.emailSentDailyAt': 'Email sent daily at {time}',
    'subscriberDetail.emailTemplate': 'Email Template',
    'subscriberDetail.defaultOption': 'Default ({name})',
    'subscriberDetail.usingTemplate': 'Using {name}',
    'subscriberDetail.usingDefault': 'Using default: {name}',
    'subscriberDetail.newsletterPreview': 'Newsletter Preview',
    'subscriberDetail.previewIframeTitle': 'Newsletter preview',
    'subscriberDetail.previewUnavailable': 'Preview unavailable',
    'subscriberDetail.topicsBuzzwords': 'Topics & Buzzwords',
    'subscriberDetail.topicsDesc': 'The AI collects articles matching these topics from the data pool and generates a personalized digest.',
    'subscriberDetail.topicCount': '{count} topic',
    'subscriberDetail.topicCountPlural': '{count} topics',
    'subscriberDetail.importing': 'Importing…',
    'subscriberDetail.importCsv': 'Import topics & buzzwords from CSV / XLSX',
    'subscriberDetail.importFormat': '2-column format: {format} — one row per buzzword',
    'subscriberDetail.importFormatToken': 'topic, buzzword',
    'subscriberDetail.importDropHint': 'Click to browse or drop a .csv / .xlsx file',
    'subscriberDetail.deleteTopicTitle': 'Delete topic',
    'subscriberDetail.noArticlesMatched': 'No articles matched yet',
    'subscriberDetail.noBuzzwordsYet': 'No buzzwords yet',
    'subscriberDetail.addBuzzwordPlaceholder': 'Add buzzword…',
    'subscriberDetail.addNewTopic': 'Add new topic',
    'subscriberDetail.topicNamePlaceholder': 'Topic name…',
    'subscriberDetail.aiSummary': 'AI will collect articles for {topicCount} and send a personalized digest to {email} daily at {time} ({timezone}).',
    'subscriberDetail.aiSummaryTopicsOne': '{count} topic',
    'subscriberDetail.aiSummaryTopicsMany': '{count} topics',
    'subscriberDetail.digestInLanguage': 'AI digest will be generated in {language}',
    'subscriberDetail.toastSettingsSaved': 'Subscriber settings saved.',
    'subscriberDetail.toastNewsletterSent': 'Newsletter sent (or queued) using the approved draft pipeline.',

    'topicMultiSelect.placeholder': 'Select topics…',
    'topicMultiSelect.empty': 'No topics yet. Create topics in the Topics screen first.',
    'topicSearchPicker.placeholder': 'Select a topic…',
    'topicSearchPicker.search': 'Search topics…',
    'topicSearchPicker.allAttached': 'All topics are already on this article.',
    'topicSearchPicker.noMatch': 'No matching topics.',

    'articleAnalysis.noArticle': 'No article selected.',
    'articleAnalysis.backToArticles': 'Back to Articles',
    'articleAnalysis.networkError': 'Something went wrong while loading this article.',
    'articleAnalysis.notFound': 'We could not load that article. It may have been removed or the link is out of date.',
    'articleAnalysis.articles': 'Articles',
    'articleAnalysis.title': 'Article Analysis',
    'articleAnalysis.subtitle': 'Inspect tags, summary, linked URLs and companies pulled from this article.',
    'articleAnalysis.dataroomSetting': 'Dataroom Setting',
    'articleAnalysis.newAnalysis': 'New Analysis',
    'articleAnalysis.newAnalysisSoon': 'New analysis coming soon',
    'articleAnalysis.indexed': 'Indexed',
    'articleAnalysis.tags': 'Tags',
    'articleAnalysis.buzzwords': 'Buzzwords',
    'articleAnalysis.summary': 'Summary',
    'articleAnalysis.companies': 'Companies',
    'articleAnalysis.noAnalysisYet': 'No analysis yet',
    'articleAnalysis.noTags': "We haven't extracted any tags from this article yet.",
    'articleAnalysis.noCompanies': "We haven't found any companies mentioned in this article.",
    'articleAnalysis.noUrl': 'No URL',
    'articleAnalysis.noSummary': 'No analysis yet.',
    'articleAnalysis.addTag': 'Add tag',
    'articleAnalysis.tagPlaceholder': 'Search and select a topic…',
    'articleAnalysis.tagAdded': 'Tag added',
    'articleAnalysis.tagRemoved': 'Tag removed',
    'articleAnalysis.removeTag': 'Remove tag',
    'articleAnalysis.tagDuplicate': 'This tag is already on the article.',
    'articleAnalysis.tagEmpty': 'Enter a tag name.',
    'articleAnalysis.addCompany': 'Add company',
    'articleAnalysis.companyPlaceholder': 'Enter company name…',
    'articleAnalysis.companyAdded': 'Company added',
    'articleAnalysis.companyDuplicate': 'This company is already on the article.',
    'articleAnalysis.companyEmpty': 'Enter a company name.',
    'articleAnalysis.articlesHome': 'Articles home',

    'dataPool.title': 'Data Pool',
    'dataPool.subtitle': 'Collected articles from all scraper sources',
    'dataPool.searchWithTopic': 'Search articles…',
    'dataPool.searchOrFilter': 'Search articles or filter by topic…',
    'dataPool.filterByTopic': 'Filter by topic',
    'dataPool.colTitle': 'Title',
    'dataPool.colUrl': 'Article URL',
    'dataPool.colTopic': 'Topic',
    'dataPool.moreTopics': '+{count} more',
    'dataPool.colTime': 'Time',
    'dataPool.colDataroom': 'DataRoom',
    'dataPool.alreadyInDataroom': 'Already in DataRoom',
    'dataPool.addToDataroom': 'Add to DataRoom',
    'dataPool.addToDataroomTitle': 'Add this article to DataRoom',
    'dataPool.emptyDate': 'No articles collected on this date',
    'dataPool.emptyFilters': 'No articles match your filters',
    'dataPool.toastApiKey': 'Moderator API key missing. Log out and sign in again as moderator.',
    'dataPool.toastAdded': 'Added to DataRoom',
    'dataPool.toastNeedAnalysis': 'Article must be analyzed before adding to DataRoom. Open and run analysis first.',
    'dataPool.toastNotFound': 'Article not found on server. Refresh Data Pool and try again.',

    'deliveryHistory.title': 'Delivery History',
    'deliveryHistory.subtitle': 'Newsletter delivery log',
    'deliveryHistory.empty': 'No delivery logs yet.',
    'deliveryHistory.colSentAt': 'Sent at',
    'deliveryHistory.logTitle': 'Log #{id}',
    'deliveryHistory.deleteTitle': 'Delete log',
    'deliveryHistory.deleteConfirm': 'Delete log?',
    'deliveryHistory.deleteDesc': 'This cannot be undone.',
    'deliveryHistory.toastLoadFailed': 'Failed to load delivery history',
    'deliveryHistory.toastDetailFailed': 'Failed to load log detail',
    'deliveryHistory.toastDeleted': 'Log deleted',
    'deliveryHistory.toastDeleteFailed': 'Failed to delete log',

    'draftsOutbox.title': 'Drafts & Outbox',
    'draftsOutbox.subtitle': 'Review AI-generated newsletter drafts before sending',
    'draftsOutbox.tabPending': 'Pending review',
    'draftsOutbox.tabAll': 'All drafts',
    'draftsOutbox.colSubject': 'Subject',
    'draftsOutbox.colCreated': 'Created',
    'draftsOutbox.empty': 'No drafts found.',
    'draftsOutbox.draftTitle': 'Draft #{id}',
    'draftsOutbox.previewTitle': 'Draft preview',
    'draftsOutbox.rejectConfirm': 'Reject draft?',
    'draftsOutbox.rejectDesc': "This draft will be marked as rejected and won't be sent.",
    'draftsOutbox.loadFailed': 'Failed to load draft',
    'draftsOutbox.toastPendingFailed': 'Failed to load pending drafts',
    'draftsOutbox.toastLoadFailed': 'Failed to load drafts',
    'draftsOutbox.toastSent': 'Draft sent successfully',
    'draftsOutbox.toastSendFailed': 'Failed to send draft',
    'draftsOutbox.toastRejected': 'Draft rejected',
    'draftsOutbox.toastRejectFailed': 'Failed to reject draft',

    'scraperPool.title': 'Scraper Pool',
    'scraperPool.subtitle': 'Your personal website sources and daily scrape schedule',
    'scraperPool.runNow': 'Run Scraper Now',
    'scraperPool.running': 'Running…',
    'scraperPool.statTotal': 'Total URLs',
    'scraperPool.statActive': 'Active',
    'scraperPool.statArticles': 'Articles in Pool',
    'scraperPool.addSource': 'Add Source',
    'scraperPool.addSourceDesc': 'Add a website URL to your scraping pool',
    'scraperPool.domainLabel': 'Domain or URL',
    'scraperPool.domainPlaceholder': 'example.com',
    'scraperPool.domainHint': 'https:// prefix is stripped automatically',
    'scraperPool.addToPool': 'Add to Pool',
    'scraperPool.tips': 'Tips',
    'scraperPool.dailySchedule': 'Daily Schedule',
    'scraperPool.autoScrapeTime': 'Automatic scrape time',
    'scraperPool.runsDaily': 'Runs every day',
    'scraperPool.autoRunsRemaining': 'auto runs left today',
    'scraperPool.autoRunsLabel': 'Auto runs left',
    'scraperPool.autoRunsResetsAt': 'Resets at {time} daily',
    'scraperPool.autoRunsRemainingOf': '{remaining} of {total} auto runs remaining',
    'scraperPool.autoRunsRemainingHint': 'Remaining automatic scrapes today out of the daily limit',
    'scraperPool.loginToSave': 'Log in to save schedule',
    'scraperPool.poolStatus': 'Pool status',
    'scraperPool.poolStatusDesc': 'Live overview of your scraper pool',
    'scraperPool.statusLabel': 'Status',
    'scraperPool.statusIdle': 'Idle',
    'scraperPool.nextRun': 'Next run',
    'scraperPool.yourSources': 'Your Sources',
    'scraperPool.searchPlaceholder': 'Search by URL or topic…',
    'scraperPool.colUrl': 'URL',
    'scraperPool.colTopics': 'Topics',
    'scraperPool.colLastRun': 'Last run',
    'scraperPool.colResult': 'Result',
    'scraperPool.colArticles': 'Articles',
    'scraperPool.noArticles': 'No articles',
    'scraperPool.unscraped': 'Unscraped',
    'scraperPool.emptySources': 'No sources yet. Add a URL to start scraping.',
    'scraperPool.emptySearch': 'No sources match your search.',
    'scraperPool.scraperRunning': 'Scraper running…',
    'scraperPool.removeTitle': 'Remove this source from your scraper pool?',
    'scraperPool.removeNote': 'Articles already collected will remain in the data pool.',
    'scraperPool.pageOf': 'Page {page} of {total}',
    'scraperPool.rangeOf': '{from}–{to} of {total}',
    'scraperPool.activePaused': '{active} active · {paused} paused',
    'scraperPool.failCount': '{n}× fail',
    'scraperPool.toastExists': 'URL already in pool',
    'scraperPool.toastAdded': 'Source added to pool',
    'scraperPool.toastRemoved': 'Source removed from pool',
    'scraperPool.toastAddFailed': 'Failed to add source',
    'scraperPool.toastRemoveFailed': 'Failed to remove source',
    'scraperPool.toastUpdateFailed': 'Failed to update source',
    'scraperPool.toastSaveFailed': 'Failed to save schedule',
    'scraperPool.toastRunFailed': 'Failed to run scraper',
    'scraperPool.quotaExtraRemaining': 'Extra runs',
    'scraperPool.quotaMaxUrls': 'Max URLs / run',
    'scraperPool.quotaMaxArticles': 'Max articles / URL',
    'scraperPool.quotaLocked': 'Locked by admin',
    'scraperPool.quotaLockedMessage': 'Scraper is disabled for this client. Please contact the administrator.',
    'scraperPool.quotaNoExtraRuns': 'No extra scraper runs are available for the current period.',
    'scraperPool.quotaMaxUrlLimit': 'You can add a maximum of {limit} URLs per scraper run.',

    'importSources.title': 'Import yourdata',
    'importSources.subtitle': 'Upload documents or Excel URL lists into your data pool',
    'importSources.tabDocument': 'Document',
    'importSources.tabExcel': 'URL list',
    'importSources.docLabel': 'PDF or Word document',
    'importSources.docHint': 'Text is extracted and added to the data pool for analysis',
    'importSources.uploadDoc': 'Upload & extract',
    'importSources.charsExtracted': '{n} characters extracted',
    'importSources.excelLabel': 'Excel file (.xlsx)',
    'importSources.excelHint': 'Paste full article URLs (not homepages). Links can sit in a URL/Link column or anywhere in the sheet.',
    'importSources.invalidSampleTitle': 'Validation issues',
    'importSources.uploadExcel': 'Upload spreadsheet',
    'importSources.sheetLabel': 'Sheet',
    'importSources.selectSheet': 'Select sheet',
    'importSources.headerRow': 'Header row',
    'importSources.preview': 'Preview',
    'importSources.validate': 'Validate',
    'importSources.confirmCrawl': 'Start crawl',
    'importSources.validCount': '{n} valid',
    'importSources.invalidCount': '{n} invalid',
    'importSources.duplicateCount': '{n} duplicate',
    'importSources.crawling': 'Crawling URLs in background…',
    'importSources.crawlFailed': 'Crawl failed — check batch details',
    'importSources.toastDocSuccess': 'Document imported successfully',
    'importSources.toastDocFailed': 'Document import failed',
    'importSources.toastExcelUploaded': 'Spreadsheet uploaded',
    'importSources.toastExcelFailed': 'Spreadsheet upload failed',
    'importSources.toastPreviewReady': 'Preview ready',
    'importSources.toastPreviewFailed': 'Preview failed',
    'importSources.toastValidated': 'Validation complete',
    'importSources.toastValidateFailed': 'Validation failed',
    'importSources.toastCrawlStarted': 'Crawl started',
    'importSources.toastConfirmFailed': 'Could not start crawl',
    'importSources.toastCrawlDone': 'All URLs crawled successfully',
    'importSources.toastCrawlPartial': 'Crawl finished with some failures',
    'importSources.toastCrawlFailed': 'Crawl failed',

    'importSources.toastDocReady': 'Document ready — confirm to import',
    'importSources.confirmDoc': 'Confirm import',
    'importSources.toastDocConfirmed': 'Document import confirmed',
    'importSources.toastDocConfirmFailed': 'Could not confirm document import',

    'campaigns.strategy.notSaved': 'Not saved yet',
    'campaigns.strategy.lastSaved': 'Last saved {date}',
    'campaigns.strategy.tzLondon': '(UTC+00:00) London, United Kingdom',
    'campaigns.strategy.tzBerlin': '(UTC+01:00) Berlin, Germany',
    'campaigns.strategy.tzHcm': '(GMT+7) Ho Chi Minh City, Vietnam',
    'campaigns.strategy.tzParis': '(UTC+01:00) Paris, France',
    'campaigns.strategy.catSaas': 'SaaS / Technology',
    'campaigns.strategy.catB2b': 'B2B Marketing',
    'campaigns.strategy.catFintech': 'FinTech',
    'campaigns.strategy.catHealthcare': 'Healthcare',
    'campaigns.strategy.errObjectiveRequired': 'Objective is required.',
    'campaigns.strategy.errAudienceRequired': 'Target audience is required.',
    'campaigns.strategy.errBrandRequired': 'Brand guidelines are required.',
    'campaigns.strategy.errFrequencyRequired': 'Posting frequency is required.',
    'campaigns.strategy.errMaxChars': 'Maximum {n} characters.',
    'campaigns.strategy.errAddOne': 'Add at least one {item}.',
    'campaigns.strategy.errMaxItems': 'Maximum {n} {item} items.',
    'campaigns.strategy.errItemMaxLen': 'Each {item} must be {n} characters or less.',
    'campaigns.strategy.itemTone': 'tone',
    'campaigns.strategy.itemPillar': 'pillar',
    'campaigns.strategy.itemMessage': 'message',
    'campaigns.strategy.itemHashtag': 'hashtag',
    'campaigns.strategy.itemTime': 'time',
    'campaigns.strategy.itemCompetitor': 'competitor',
    'campaigns.strategy.previewBrand': 'Brand positioning: {text}',
    'campaigns.strategy.previewPillars': 'Content pillars: {text}',
    'campaigns.strategy.previewTone': 'Tone of voice: {text}',
    'campaigns.strategy.previewMessages': 'Key messages: {text}',
    'campaigns.strategy.previewHashtags': 'Hashtag plan: {text}',

    'linkedInSchedule.title': 'LinkedIn Schedule',
    'linkedInSchedule.subtitle': 'Plan, organize and automate campaign posts across upcoming dates.',
    'linkedInSchedule.autoSchedule': 'Auto Schedule',
    'linkedInSchedule.newCampaign': 'New Campaign',
    'linkedInSchedule.newPost': 'New Post',
    'linkedInSchedule.allCampaigns': 'All Campaigns',
    'linkedInSchedule.allStatus': 'All Status',
    'linkedInSchedule.allLanguages': 'All Languages',
    'linkedInSchedule.searchPlaceholder': 'Search posts, titles, or campaigns…',
    'linkedInSchedule.prevDays': 'Previous days',
    'linkedInSchedule.nextDays': 'Next days',
    'linkedInSchedule.statTotalScheduled': 'Total Scheduled Posts',
    'linkedInSchedule.statDraftsPending': 'Drafts Pending Approval',
    'linkedInSchedule.statActiveCampaigns': 'Active Campaigns',
    'linkedInSchedule.statUpcomingAutoPosts': 'Upcoming Auto-Posts',
    'linkedInSchedule.statPostingFrequency': 'Posting Frequency',
    'linkedInSchedule.statGrowth': '+12% vs last 7 days',
    'linkedInSchedule.statNeedsReview': 'Needs review',
    'linkedInSchedule.statRunningPaused': '{active} running · {paused} paused',
    'linkedInSchedule.statNextPost': 'Next: Today, 10:00 AM',
    'linkedInSchedule.statFrequencyValue': '4.2 posts/day',
    'linkedInSchedule.statOptimal': 'Optimal',
    'linkedInSchedule.gridCampaigns': 'Campaigns',
    'linkedInSchedule.postsProgress': '{approved} / {total} posts',
    'linkedInSchedule.dragHint': 'Drag and drop posts to reschedule',
    'linkedInSchedule.viewMonth': 'Month',
    'linkedInSchedule.viewWeek': 'Week',
    'linkedInSchedule.viewList': 'List',
    'linkedInSchedule.draft': 'Draft',
    'linkedInSchedule.postDetails': 'Post Details',
    'linkedInSchedule.dayDetails': '{day} Details',
    'linkedInSchedule.postTitle': 'Post Title',
    'linkedInSchedule.language': 'Language',
    'linkedInSchedule.scheduledDateTime': 'Scheduled Date & Time',
    'linkedInSchedule.campaign': 'Campaign',
    'linkedInSchedule.objectiveCta': 'Objective / CTA',
    'linkedInSchedule.approval': 'Approval',
    'linkedInSchedule.schedule': 'Schedule',
    'linkedInSchedule.publishNow': 'Publish Now',
    'linkedInSchedule.saveDraft': 'Save Draft',
    'linkedInSchedule.deletePost': 'Delete Post',
    'linkedInSchedule.storedPosts': 'Stored posts',
    'linkedInSchedule.draftEmpty': 'Drag posts here to store in Draft.',
    'linkedInSchedule.dayPostsCount': '{count} post(s) on {day}',
    'linkedInSchedule.noPostsDay': 'No posts scheduled for this day.',
    'linkedInSchedule.hoverLocation': 'Location',
    'linkedInSchedule.hoverRole': 'Role',
    'linkedInSchedule.hoverTime': 'Time',
    'linkedInSchedule.hoverDate': 'Date',
    'linkedInSchedule.scheduleConflict': 'Schedule conflict at this time',
    'linkedInSchedule.linkedInAccount': 'LinkedIn Account',
    'linkedInSchedule.removeFromDraft': 'Remove {title} from draft',
    'linkedInSchedule.datePickerTitle': 'Duration Range Picker',
    'linkedInSchedule.chooseStartDate': 'Choose a start date',
    'linkedInSchedule.chooseEndDate': 'Choose an end date',
    'linkedInSchedule.closeDatePicker': 'Close date range picker',
    'linkedInSchedule.prevMonth': 'Previous month',
    'linkedInSchedule.nextMonth': 'Next month',
    'linkedInSchedule.startDate': 'Start date',
    'linkedInSchedule.endDate': 'End date',
    'linkedInSchedule.clearDates': 'Clear',
    'linkedInSchedule.saveDates': 'Save Dates',
    'linkedInSchedule.approval.approved': 'Approved',
    'linkedInSchedule.approval.pending': 'Pending',
    'linkedInSchedule.approval.rejected': 'Rejected',
    'campaigns.tab.overview': 'Overview',
    'campaigns.tab.content': 'Content',
    'campaigns.tab.strategy': 'Strategy',
    'campaigns.tab.schedule': 'Schedule',
    'campaigns.tab.analytics': 'Analytics',
    'campaigns.tab.settings': 'Settings',
    'campaigns.title': 'LinkedIn',
    'campaigns.newLinkedIn': 'New LinkedIn',
    'campaigns.searchPlaceholder': 'Search LinkedIn…',
    'campaigns.allStatus': 'All Status',
    'campaigns.campaignCount': '{count} campaign(s)',
    'campaigns.status.active': 'Active',
    'campaigns.status.completed': 'Completed',
    'campaigns.preview': 'Preview',
    'campaigns.edit': 'Edit',
    'campaigns.createdOn': 'Created on {date}',
    'campaigns.type.awareness': 'Awareness',
    'campaigns.type.productLaunch': 'Product Launch',
    'campaigns.type.engagement': 'Engagement',
    'campaigns.type.content': 'Content',
    'campaigns.type.event': 'Event',
    'campaigns.type.recruitment': 'Recruitment',
    'campaigns.type.thoughtLeadership': 'Thought Leadership',
    'campaigns.contentReview': 'Content Review',
    'campaigns.contentReviewDesc': 'Review and approve your content before scheduling',
    'campaigns.language': 'Language',
    'campaigns.posts': 'Posts',
    'campaigns.postsInCampaign': '{count} in this campaign',
    'campaigns.addNewPost': 'Add New Post',
    'campaigns.postTitle': 'Post Title',
    'campaigns.content': 'Content',
    'campaigns.media': 'Media',
    'campaigns.noImages': 'No images',
    'campaigns.imageCount': '{count} image(s)',
    'campaigns.uploadedMedia': 'Uploaded media',
    'campaigns.callToAction': 'Call to Action',
    'campaigns.cta.learnMore': 'Learn More',
    'campaigns.cta.signUp': 'Sign Up',
    'campaigns.cta.contact': 'Contact Us',
    'campaigns.cta.download': 'Download',
    'campaigns.saveDraft': 'Save Draft',
    'campaigns.approveSchedule': 'Approve & Schedule',
    'campaigns.noPostSelected': 'No post selected',
    'campaigns.addFirstPost': 'Click "Add New Post" to create your first post',
    'campaigns.comingSoon': 'This section is coming soon',
    'campaigns.aiStrategyAssistant': 'AI Strategy Assistant',
    'campaigns.aiContentAssistant': 'AI Content Assistant',
    'campaigns.newChat': 'New Chat',
    'campaigns.strategyOverview': 'Strategy Overview',
    'campaigns.strategyOverviewDesc': 'Define the content strategy for your LinkedIn campaign. This strategy will be used as context for AI content generation.',
    'campaigns.fixRequired': 'Fix required fields',
    'campaigns.unsavedChanges': 'Unsaved changes',
    'campaigns.objective': 'Campaign Objective',
    'campaigns.targetAudience': 'Target Audience',
    'campaigns.category': 'Category',
    'campaigns.toneOfVoice': 'Tone of Voice',
    'campaigns.tonePlaceholder': 'Add tone (e.g. Professional)',
    'campaigns.toneNotesPlaceholder': 'Optional tone notes…',
    'campaigns.brandGuidelines': 'Brand Guidelines',
    'campaigns.competitorPlaceholder': 'Add competitor',
    'campaigns.contentPillars': 'Content Pillars',
    'campaigns.addPillar': 'Add pillar',
    'campaigns.keyMessages': 'Key Messages',
    'campaigns.addMessage': 'Add message',
    'campaigns.hashtagStrategy': 'Hashtag Strategy',
    'campaigns.hashtagPlaceholder': 'Add hashtag',
    'campaigns.hashtagNotesPlaceholder': 'Hashtag usage notes…',
    'campaigns.postingFrequency': 'Posting Frequency',
    'campaigns.preferredPostingTime': 'Preferred Posting Time',
    'campaigns.strategyDirtyHint': 'Save to apply strategy for AI per-post generation.',
    'campaigns.strategySynced': 'Strategy is in sync with last save.',
    'campaigns.generating': 'Generating…',
    'campaigns.generateStrategyAi': 'Generate Strategy with AI',
    'campaigns.saveStrategy': 'Save Strategy',
    'campaigns.resetChanges': 'Reset Changes',
    'campaigns.saving': 'Saving…',
    'campaigns.generateStrategy': 'Generate Strategy',
    'campaigns.suggestPillars': 'Suggest Content Pillars',
    'campaigns.suggestPillarsDesc': 'Get AI suggestions for content pillars based on your objective.',
    'campaigns.suggestMessages': 'Suggest Key Messages',
    'campaigns.suggestMessagesDesc': 'Generate key messages that resonate with your audience.',
    'campaigns.suggestHashtags': 'Suggest Hashtags',
    'campaigns.suggestHashtagsDesc': 'Find relevant hashtags for better reach and engagement.',
    'campaigns.suggestTimes': 'Suggest Posting Times',
    'campaigns.suggestTimesDesc': 'Get optimal posting times based on your audience.',
    'campaigns.askAnything': 'Ask me anything…',
    'campaigns.addChip': 'Add chip',
    'campaigns.addListItem': 'Add list item',
    'campaigns.addItem': 'Add item',
    'campaigns.toastStrategySaved': 'Strategy saved. New posts will use this context for AI per-post generation.',
    'campaigns.scheduleTitle': 'LinkedIn Schedule',
    'campaigns.scheduleSubtitle': 'Drag and drop posts to schedule or reschedule',
    'campaigns.prevWeek': 'Previous week',
    'campaigns.nextWeek': 'Next week',
    'campaigns.showUnscheduled': 'Show Unscheduled',
    'campaigns.allDay': 'All day',
    'campaigns.conflict': 'Conflict',
    'campaigns.postsOnCalendar': '{name} — {count} posts on one calendar',
    'campaigns.scheduledUnscheduled': '{scheduled} scheduled · {unscheduled} unscheduled',
    'campaigns.draggingPost': 'Dragging post…',
    'campaigns.schedulingConflicts': '{count} scheduling conflict(s)',
    'campaigns.scheduleRules': 'Schedule Rules',
    'campaigns.scheduleRulesDesc': 'Max {maxPerDay} posts/day · min {minHours}h apart · posted posts cannot move. Click a post to open it in Content.',
    'campaigns.unscheduledPosts': 'Unscheduled Posts',
    'campaigns.dropToUnschedule': 'Drop here to unschedule',
    'campaigns.noUnscheduledPosts': 'No unscheduled posts',
    'campaigns.errPostedCannotReschedule': 'Posted posts cannot be rescheduled',
    'campaigns.overview.campaignStatus': 'Campaign Status',
    'campaigns.overview.objective': 'Objective',
    'campaigns.overview.targetAudience': 'Target Audience',
    'campaigns.overview.category': 'Category',
    'campaigns.overview.owner': 'Owner',
    'campaigns.overview.postStatusSummary': 'Post Status Summary',
    'campaigns.overview.campaignProgress': 'Campaign Progress',
    'campaigns.overview.schedulePreview': 'Schedule Preview',
    'campaigns.overview.strategySnapshot': 'Strategy Snapshot',
    'campaigns.overview.topHashtags': 'Top Hashtags',
    'campaigns.overview.analyticsSnapshot': 'Analytics Snapshot',
    'campaigns.overview.lastUpdated': 'Last updated: {date}',
    'campaigns.overview.recentActivity': 'Recent Activity',
    'campaigns.overview.viewProgress': 'View progress details +',
    'campaigns.overview.viewSchedule': 'View full schedule +',
    'campaigns.overview.viewStrategy': 'View strategy +',
    'campaigns.overview.viewAnalytics': 'View analytics +',
    'campaigns.overview.viewActivity': 'View all activity +',
    'campaigns.overview.openSchedule': 'Open full schedule',
    'campaigns.overview.impressions': 'Impressions',
    'campaigns.overview.clicks': 'Clicks',
    'campaigns.overview.reactions': 'Reactions',
    'campaigns.overview.comments': 'Comments',
    'campaigns.overview.shares': 'Shares',
    'campaigns.overview.engagementRate': 'Engagement Rate',
    'campaigns.overview.archived': 'Archived',
    'campaigns.overview.nextPost': 'Next Post',
    'campaigns.overview.following': 'Following',
    'campaigns.overview.viewAllPosts': 'View all posts +',
    'campaigns.overview.totalPosts': 'total posts',
    'campaigns.overview.completionRate': 'Completion Rate',
    'campaigns.overview.totalPostsLabel': 'Total Posts',
    'campaigns.overview.members': '{count} members',
    'campaigns.overview.moreHashtags': '+{count} more',
    'campaigns.overview.daysRemaining': '{count} days remaining',
    'campaigns.overview.activity.postApproved': 'Post {post} was approved',
    'campaigns.overview.activity.postScheduled': 'Post {post} was scheduled',
    'campaigns.overview.activity.postPublished': 'Post {post} was published',
    'campaigns.overview.activity.aiApplied': 'AI content suggestions applied to Post {post}',
    'campaigns.overview.activity.settingsUpdated': 'Campaign settings were updated',
    'campaigns.settings.general': 'General Settings',
    'campaigns.settings.generalDesc': 'Manage basic information and default preferences for this campaign.',
    'campaigns.settings.campaignName': 'Campaign Name',
    'campaigns.settings.defaultLanguage': 'Default Language',
    'campaigns.settings.timeZone': 'Time Zone',
    'campaigns.settings.approval': 'Approval Workflow',
    'campaigns.settings.approvalDesc': 'Configure who can review and approve content before publishing.',
    'campaigns.settings.reviewers': 'Reviewers',
    'campaigns.settings.approvers': 'Approvers',
    'campaigns.settings.autoApproveDrafts': 'Auto-approve Drafts',
    'campaigns.settings.reminder': 'Reminder',
    'campaigns.settings.approvalNote': 'Draft content will require manual approval.',
    'campaigns.settings.notifications': 'Notifications',
    'campaigns.settings.notificationsDesc': 'Choose what updates you want to receive.',
    'campaigns.settings.notifyApproval': 'Content approval requests',
    'campaigns.settings.notifyReminders': 'Scheduled post reminders',
    'campaigns.settings.notifyPerformance': 'Post performance alerts',
    'campaigns.settings.notifyStatus': 'Campaign status changes',
    'campaigns.settings.integrations': 'Integrations',
    'campaigns.settings.integrationsDesc': 'Connect external tools and services.',
    'campaigns.settings.connected': 'Connected',
    'campaigns.settings.manage': 'Manage',
    'campaigns.settings.privacy': 'Data & Privacy',
    'campaigns.settings.privacyDesc': 'Manage data retention and privacy preferences.',
    'campaigns.settings.dataRetention': 'Data Retention',
    'campaigns.settings.anonymize': 'Anonymize Analytics Data',
    'campaigns.settings.anonymizeDesc': 'Remove personally identifiable information.',
    'campaigns.settings.dangerZone': 'Danger Zone',
    'campaigns.settings.dangerDesc': 'Irreversible and sensitive actions.',
    'campaigns.settings.archiveCampaign': 'Archive Campaign',
    'campaigns.settings.archiveDesc': 'This will archive the campaign and all related content.',
    'campaigns.settings.archive': 'Archive',
    'campaigns.settings.deleteCampaign': 'Delete Campaign',
    'campaigns.settings.deleteDesc': 'This action cannot be undone. All data will be permanently deleted.',
    'campaigns.settings.saveChanges': 'Save Changes',
    'campaigns.settings.saving': 'Saving…',
    'campaigns.datePicker.chooseStart': 'Choose a start date',
    'campaigns.datePicker.chooseEnd': 'Choose an end date',
    'campaigns.datePicker.reference': 'Reference from LinkedIn Schedule',
    'campaigns.datePicker.close': 'Close date range picker',
    'campaigns.datePicker.prevMonth': 'Previous month',
    'campaigns.datePicker.nextMonth': 'Next month',

    'linkedInSchedule.status.scheduled': 'Scheduled',
    'linkedInSchedule.status.draft': 'Draft',
    'linkedInSchedule.status.approved': 'Approved',
    'linkedInSchedule.status.posted': 'Posted',

    'dataroom.selectedArticles': 'Selected articles ({count})',
    'dataroom.selectionHint': 'Click an article in DataRoom to select, or drag and drop here.',
    'dataroom.noArticlesSelected': 'No articles selected yet.',
    'dataroom.deselectArticle': 'Deselect article',
    'dataroom.breadcrumbDataRooms': 'DataRooms',
    'dataroom.pageTitle': 'Dataroom Setting',
    'dataroom.libraryAriaLabel': 'Dataroom articles',
    'dataroom.deliveryPublishingTitle': 'Delivery & Publishing',
    'dataroom.deliveryPublishingSubtitle': 'Configure how this DataRoom digest will be generated, reviewed, and delivered.',
    'dataroom.dataroomTitle': 'DataRoom',
    'dataroom.savedArticles': '{count} saved article',
    'dataroom.savedArticlesPlural': '{count} saved articles',
    'dataroom.searchDataroom': 'Search DataRoom...',
    'dataroom.searchNoResults': 'No articles match your search.',
    'dataroom.collectedTopicsAria': 'Collected topics',
    'dataroom.noTopicsYet': 'No topics yet',
    'dataroom.collectedArticlesEmpty': 'Collected articles will appear here.',
    'dataroom.removeArticleFromDataroom': 'Remove {title} from DataRoom',
    'dataroom.general': 'General',
    'dataroom.sendNow': 'Send Now',
    'dataroom.scheduleSend': 'Schedule Send',
    'dataroom.exportFile': 'Export File',
    'dataroom.sendNowDesc': 'Generate and send the digest immediately.',
    'dataroom.scheduleSendDesc': 'Send once or repeatedly at a configured time.',
    'dataroom.exportFileDesc': 'Download digest as PDF, DOCX or CSV.',
    'dataroom.deliveryMethodAria': 'Delivery method',
    'dataroom.saveDraft': 'Save Draft',
    'dataroom.sendEmailNow': 'Send Email Now',
    'dataroom.downloadFile': 'Download File',
    'dataroom.working': 'Working…',
    'dataroom.configuration': 'Configuration',
    'dataroom.recipients': 'Recipients',
    'dataroom.recipientsSelected': '{count} recipient selected',
    'dataroom.recipientsSelectedPlural': '{count} recipients selected',
    'dataroom.searchSubscriberPlaceholder': 'Search subscriber by name or email…',
    'dataroom.searchByNameOrEmail': 'Search by name or email…',
    'dataroom.selectAll': 'Select all',
    'dataroom.clear': 'Clear',
    'dataroom.loadingSubscribers': 'Loading subscribers…',
    'dataroom.noSubscribersFound': 'No subscribers found.',
    'dataroom.selectedRecipients': 'Selected recipients ({count})',
    'dataroom.removeRecipient': 'Remove {email}',
    'dataroom.emailSubject': 'Email subject',
    'dataroom.sendNowInfo': 'This digest will be sent immediately after you review the preview and click Send Email Now.',
    'dataroom.scheduleType': 'Schedule type',
    'dataroom.oneTime': 'One-time',
    'dataroom.recurring': 'Recurring',
    'dataroom.date': 'Date',
    'dataroom.time': 'Time',
    'dataroom.timezone': 'Timezone',
    'dataroom.recurringInfo': 'Recurring schedules repeat daily at the selected time (backend recurring job).',
    'dataroom.exportFormat': 'Export format',
    'dataroom.fileContent': 'File content',
    'dataroom.includeArticleSummary': 'Include article summary',
    'dataroom.includeSourceLinks': 'Include source links',
    'dataroom.includeTopicGrouping': 'Include topic grouping',
    'dataroom.includeRawUrlsOnly': 'Include raw article URLs only',
    'dataroom.fileName': 'File name',
    'dataroom.previewValidation': 'Preview & Validation',
    'dataroom.generate': 'Generate',
    'dataroom.generating': 'Generating…',
    'dataroom.previewGenerated': 'Preview generated',
    'dataroom.regenerate': 'Regenerate',
    'dataroom.regenerating': 'Regenerating…',
    'dataroom.generatingPreviewAi': 'Generating preview with AI…',
    'dataroom.regeneratingPreview': 'Regenerating preview…',
    'dataroom.generatedAt': 'Generated at',
    'dataroom.articlesIncluded': 'Articles included',
    'dataroom.articlesCount': '{count} articles',
    'dataroom.topics': 'Topics',
    'dataroom.recipientsCount': '{count} recipients',
    'dataroom.reviewBeforeSend': 'Please review the preview before sending.',
    'dataroom.subject': 'Subject',
    'dataroom.openFullPreview': 'Open Full Preview',
    'dataroom.fullPreviewAria': 'Full preview',
    'dataroom.previewDeviceSizeAria': 'Preview device size',
    'dataroom.fullDigestPreview': 'Full digest preview - {device}',
    'dataroom.selectDate': 'Select date',
    'dataroom.selectTime': 'Select time',
    'dataroom.previousMonth': 'Previous month',
    'dataroom.nextMonth': 'Next month',
    'dataroom.weekdaySu': 'Su',
    'dataroom.weekdayMo': 'Mo',
    'dataroom.weekdayTu': 'Tu',
    'dataroom.weekdayWe': 'We',
    'dataroom.weekdayTh': 'Th',
    'dataroom.weekdayFr': 'Fr',
    'dataroom.weekdaySa': 'Sa',
    'dataroom.hour': 'Hour',
    'dataroom.minute': 'Minute',
    'dataroom.now': 'Now',
    'dataroom.am': 'AM',
    'dataroom.pm': 'PM',
    'dataroom.deviceIphone': 'iPhone',
    'dataroom.deviceAndroid': 'Android',
    'dataroom.devicePc': 'PC',
    'dataroom.assistantAriaLabel': 'AI content assistant',
    'dataroom.aiContentAssistant': 'AI Content Assistant',
    'dataroom.newChat': 'New Chat',
    'dataroom.thinking': 'Thinking…',
    'dataroom.messagePlaceholder': 'Type your message...',
    'dataroom.messageAriaLabel': 'AI assistant message',
    'dataroom.sendMessageAria': 'Send message',
    'dataroom.assistantWelcome': 'Hi! Drag articles from DataRoom into this chat to import them as documents, then ask questions about them. You can also ask about delivery setup or preview readiness.',
    'dataroom.assistantRequestFailed': 'Assistant request failed. Please try again.',
    'dataroom.assistantNoArticles': 'Add at least one article to the DataRoom before asking the assistant for help.',
    'dataroom.assistantDropHint': 'Drag a DataRoom article here to import it into this chat.',
    'dataroom.assistantAttachedAria': 'Articles imported into this chat',
    'dataroom.assistantImporting': 'Importing article…',
    'dataroom.assistantImportedArticle': 'Imported “{title}” ({chunks} chunks). Ask me anything about it.',
    'dataroom.assistantImportSuccess': 'Imported “{title}” into the chat.',
    'dataroom.assistantImportFailed': 'Could not import the article into the chat.',
    'dataroom.assistantAlreadyImported': '“{title}” is already imported in this chat.',
    'dataroom.assistantDropUnknownArticle': 'That article is no longer in the DataRoom.',
    'dataroom.validationNoArticles': 'Add at least one article to the DataRoom before generating a preview.',
    'dataroom.validationNoSelected': 'Select or drag at least one article into Preview & Validation.',
    'dataroom.validationGeneratePreview': 'Generate a preview before sending, scheduling, or exporting.',
    'dataroom.validationScheduleDateTime': 'Choose a schedule date and time before scheduling.',
    'dataroom.defaultEmailSubject': 'Daily HAWK Digest - {date}',
    'dataroom.toastSelectRecipient': 'Select at least one recipient.',
    'dataroom.toastSelectArticle': 'Select at least one article for delivery.',
    'dataroom.toastGeneratePreview': 'Generate a preview before sending.',
    'dataroom.toastGenerateBeforeSchedule': 'Generate a preview before scheduling.',
    'dataroom.toastApiKeyMissing': 'Moderator API key missing. Log out and sign in again as moderator.',
    'dataroom.toastStaleArticlesRemoved': '{count} outdated article(s) removed from DataRoom ({titles}). Re-add them from Data Pool.',
    'dataroom.toastSentSuccess': 'Sent to {count} subscriber',
    'dataroom.toastSentSuccessPlural': 'Sent to {count} subscribers',
    'dataroom.toastSentPartial': 'Sent to {success}, failed for {failed}. {error}',
    'dataroom.toastSendFailed': 'Send failed. Please try again.',
    'dataroom.toastChooseSchedule': 'Choose a schedule date and time.',
    'dataroom.toastScheduledSuccess': 'Scheduled for {count} subscriber',
    'dataroom.toastScheduledSuccessPlural': 'Scheduled for {count} subscribers',
    'dataroom.toastScheduledPartial': 'Scheduled for {success}, failed for {failed}. {error}',
    'dataroom.toastScheduleFailed': 'Schedule failed. Please try again.',
    'dataroom.toastCsvDownloaded': 'CSV file downloaded.',
    'dataroom.toastPdfPrint': 'Print dialog opened for PDF export.',
    'dataroom.toastDocDownloaded': 'Document downloaded (HTML-based .doc).',
    'dataroom.toastExportFailed': 'Export failed. Please try again.',
    'dataroom.toastDraftSaved': 'Delivery settings saved as draft.',
    'dataroom.toastDraftSaveFailed': 'Could not save draft.',
    'dataroom.errGenerationFailed': 'Generation failed. Please try again.',
    'dataroom.errStaleArticles': '{count} article(s) are no longer on the server ({titles}). They were removed — re-add from Data Pool, then generate preview again.',
    'dataroom.errNoValidArticles': 'No valid articles to send. Re-add articles from Data Pool.',
    'dataroom.errArticleNotAnalyzed': '"{title}" has not been analyzed yet. Run analysis in Data Pool before sending.',
    'dataroom.errSyncArticle': 'Could not sync "{title}" (ID {id}): {reason}',
    'dataroom.errDispatchNoHistory': 'Dispatch did not return a history id.',
    'dataroom.errEmailDeliveryFailed': 'Email delivery failed on the server (SMTP).',
    'dataroom.errDispatchStatus': 'Dispatch finished with status "{status}".',
  },

  de: {
    'common.cancel': 'Abbrechen',
    'common.save': 'Speichern',
    'common.delete': 'Löschen',
    'common.close': 'Schließen',
    'common.loading': 'Laden',
    'common.retry': 'Erneut versuchen',
    'common.all': 'Alle',
    'common.previous': 'Zurück',
    'common.next': 'Weiter',
    'common.today': 'Heute',
    'common.tomorrow': 'Morgen',
    'common.yesterday': 'Gestern',
    'common.subscriber': 'Abonnent',
    'common.template': 'Vorlage',
    'common.status': 'Status',
    'common.actions': 'Aktionen',
    'common.open': 'Öffnen',
    'common.search': 'Suchen',
    'common.clearFilters': 'Filter zurücksetzen',
    'common.never': 'Nie',
    'common.justNow': 'Gerade eben',
    'common.minAgo': 'vor {n} Min.',
    'common.hoursAgo': 'vor {n} Std.',
    'common.daysAgo': 'vor {n} Tag(en)',
    'common.unknownTime': 'unbekannte Zeit',
    'common.secondsAgo': 'vor {n}s',
    'common.minutesAgoShort': 'vor {n}m',
    'common.hoursAgoShort': 'vor {n}h',
    'common.daysAgoShort': 'vor {n}d',
    'common.articles': '{count} Artikel',
    'common.articlesOf': '{shown} von {total} Artikeln',
    'common.articlesToday': '{count} Artikel heute',
    'common.optional': '(optional)',
    'common.reject': 'Ablehnen',
    'common.send': 'Senden',
    'common.remove': 'Entfernen',
    'common.saved': 'Gespeichert',
    'common.dismiss': 'Schließen',
    'common.off': 'Aus',
    'common.paused': 'Pausiert',
    'common.success': 'Erfolg',
    'common.failed': 'Fehlgeschlagen',
    'common.uncategorized': 'Ohne Kategorie',
    'common.articleNumber': 'Artikel #{id}',
    'common.create': 'Erstellen',
    'common.edit': 'Bearbeiten',
    'common.reset': 'Zurücksetzen',
    'common.email': 'E-Mail',
    'common.username': 'Benutzername',
    'common.language': 'Sprache',
    'common.password': 'Passwort',
    'common.saving': 'Speichern…',
    'common.creating': 'Erstellen…',
    'common.adding': 'Hinzufügen…',
    'common.removing': 'Entfernen…',
    'common.active': 'Aktiv',
    'common.inactive': 'Inaktiv',
    'common.system': 'System',
    'common.custom': 'Benutzerdefiniert',
    'common.default': 'Standard',
    'common.topics': 'Themen',
    'common.buzzwords': 'Schlagwörter',
    'common.keywords': 'Schlüsselwörter',
    'common.preview': 'Vorschau',
    'common.duplicate': 'Duplizieren',
    'common.description': 'Beschreibung',
    'common.frequency': 'Häufigkeit',
    'common.timezone': 'Zeitzone',
    'common.profile': 'Profil',
    'common.memberSince': 'Mitglied seit',
    'common.daily': 'Täglich',
    'common.topic': 'Thema',
    'common.schedule': 'Zeitplan',
    'common.regenerate': 'Neu generieren',
    'common.showingRange': '{from}–{to} von {total}',

    'emailTemplates.title': 'E-Mail-Vorlagen',
    'emailTemplates.subtitle': 'E-Mail-Vorlagen auswählen, ansehen, anpassen und Abonnenten zuweisen.',
    'emailTemplates.importTemplate': 'Vorlage importieren',
    'emailTemplates.newCustomTemplate': 'Neue benutzerdefinierte Vorlage',
    'emailTemplates.toastImportSoon': 'Import-Funktion demnächst verfügbar.',
    'emailTemplates.loadErrorTitle': 'Vorlagen konnten nicht von der API geladen werden',
    'emailTemplates.toastLoadTemplates': 'E-Mail-Vorlagen konnten nicht geladen werden: {msg}',
    'emailTemplates.toastLoadSubscribers': 'Abonnenten konnten nicht geladen werden: {msg}',
    'emailTemplates.statTotal': 'Vorlagen gesamt',
    'emailTemplates.statTotalSub': 'Alle E-Mail-Vorlagen',
    'emailTemplates.statSystem': 'Systemvorlagen',
    'emailTemplates.statSystemSub': 'Vom System bereitgestellt',
    'emailTemplates.statCustom': 'Benutzerdefinierte Vorlagen',
    'emailTemplates.statCustomSub': 'Von Ihnen erstellt',
    'emailTemplates.statAssigned': 'Zugewiesene Abonnenten',
    'emailTemplates.statAssignedSub': 'Mit zugewiesener Vorlage',
    'emailTemplates.libraryTitle': 'Vorlagenbibliothek',
    'emailTemplates.searchPlaceholder': 'Vorlagen suchen…',
    'emailTemplates.tab.all': 'Alle',
    'emailTemplates.tab.system': 'System',
    'emailTemplates.tab.custom': 'Benutzerdefiniert',
    'emailTemplates.tab.shared': 'Geteilt',
    'emailTemplates.badge.default': 'Standard',
    'emailTemplates.badge.shared': '@Geteilt',
    'emailTemplates.usedByOne': 'Verwendet von {count} Abonnent',
    'emailTemplates.usedByMany': 'Verwendet von {count} Abonnenten',
    'emailTemplates.emptyTemplates': 'Keine Vorlagen gefunden.',
    'emailTemplates.showingTemplates': '1–{shown} von {total} Vorlagen',
    'emailTemplates.detailsTitle': 'Vorlagendetails & Vorschau',
    'emailTemplates.usedByLabel': 'Verwendet von',
    'emailTemplates.subscriberOne': '{count} Abonnent',
    'emailTemplates.subscriberMany': '{count} Abonnenten',
    'emailTemplates.lastUpdated': 'Zuletzt aktualisiert',
    'emailTemplates.createdBy': 'Erstellt von',
    'emailTemplates.createdBySystem': 'System',
    'emailTemplates.createdByYou': 'Sie',
    'emailTemplates.actionsTitle': 'Aktionen',
    'emailTemplates.setAsDefault': 'Als Standard festlegen',
    'emailTemplates.sendTestEmail': 'Test-E-Mail senden',
    'emailTemplates.toastDuplicateSoon': 'Duplizieren demnächst verfügbar.',
    'emailTemplates.toastTestEmailSoon': 'Test-E-Mail demnächst verfügbar.',
    'emailTemplates.selectToViewDetails': 'Vorlage auswählen, um Details anzuzeigen',
    'emailTemplates.previewTitle': 'Vorschau',
    'emailTemplates.desktop': 'Desktop',
    'emailTemplates.mobile': 'Mobil',
    'emailTemplates.fromLabel': 'Von: hawk@digest.io',
    'emailTemplates.viewInBrowser': 'Im Browser ansehen',
    'emailTemplates.previewDigestLine': '— Ihr personalisierter Digest-Inhalt',
    'emailTemplates.previewPoweredLine': '— Powered by Hawk AI',
    'emailTemplates.moreArticles': '+ weitere Artikel personalisiert für jeden Abonnenten…',
    'emailTemplates.poweredBy': 'Powered by Hawk AI',
    'emailTemplates.selectToPreview': 'Vorlage auswählen für Vorschau',
    'emailTemplates.assignmentsTitle': 'Vorlagenzuweisungen für Abonnenten',
    'emailTemplates.assignmentsSubtitle': 'Vorlagen Abonnenten zuweisen. Abonnenten ohne Zuweisung nutzen die Standardvorlage.',
    'emailTemplates.searchSubscriber': 'Abonnent suchen…',
    'emailTemplates.filterTemplate': 'Vorlage',
    'emailTemplates.filterStatus': 'Status',
    'emailTemplates.statusSaved': 'Gespeichert',
    'emailTemplates.statusUnsaved': 'Nicht gespeichert',
    'emailTemplates.bulkAssign': 'Massenzuweisung',
    'emailTemplates.toastBulkAssignSoon': 'Massenzuweisung demnächst verfügbar.',
    'emailTemplates.colAssignedTemplate': 'Zugewiesene Vorlage',
    'emailTemplates.colTemplateType': 'Vorlagentyp',
    'emailTemplates.colLastUpdated': 'Zuletzt aktualisiert',
    'emailTemplates.changeTemplate': 'Vorlage ändern',
    'emailTemplates.resetToDefault': 'Auf Standard zurücksetzen',
    'emailTemplates.emptySubscribers': 'Keine Abonnenten gefunden.',
    'emailTemplates.showingSubscribers': '1–{shown} von {total} Abonnenten',
    'emailTemplates.dialogNewTitle': 'Neue benutzerdefinierte Vorlage',
    'emailTemplates.dialogEditTitle': 'Vorlage bearbeiten',
    'emailTemplates.saveChanges': 'Änderungen speichern',
    'emailTemplates.deleteTitle': 'Vorlage löschen?',
    'emailTemplates.deleteDesc': '{name} löschen? Abonnenten mit dieser Vorlage wechseln zur Standardvorlage. Dies kann nicht rückgängig gemacht werden.',
    'emailTemplates.changeAssignmentTitle': 'Vorlagenzuweisung ändern',
    'emailTemplates.changeAssignmentDesc': 'Vorlage zuweisen an {email}',
    'emailTemplates.useDefault': 'Standard verwenden',
    'emailTemplates.defaultSuffix': '(Standard)',
    'emailTemplates.toastCreated': 'Vorlage erstellt.',
    'emailTemplates.toastUpdated': 'Vorlage aktualisiert.',
    'emailTemplates.toastDefaultUpdated': 'Standardvorlage aktualisiert.',
    'emailTemplates.toastDeleted': 'Vorlage gelöscht.',
    'emailTemplates.toastResetAssignment': 'Auf Standardvorlage zurückgesetzt.',
    'emailTemplates.toastAssignmentUpdated': 'Vorlagenzuweisung aktualisiert.',
    'emailTemplates.formName': 'Vorlagenname',
    'emailTemplates.formNamePlaceholder': 'z. B. Mein Custom Digest',
    'emailTemplates.formDescriptionPlaceholder': 'Kurze Beschreibung dieses Layouts',
    'emailTemplates.formTag': 'Tag',
    'emailTemplates.formTagPlaceholder': 'z. B. minimal, tech, finance',
    'emailTemplates.formAccent': 'Akzentfarbe',
    'emailTemplates.accent.blue': 'Blau',
    'emailTemplates.accent.amber': 'Bernstein',
    'emailTemplates.accent.cyan': 'Cyan',
    'emailTemplates.accent.rose': 'Rosa',

    'subscribers.title': 'Abonnenten',
    'subscribers.subtitle': 'Empfänger, Versandpläne und Newsletter-Einstellungen verwalten.',
    'subscribers.importCsv': 'CSV importieren',
    'subscribers.toastImportCsvSoon': 'CSV-Import demnächst verfügbar.',
    'subscribers.addSubscriber': 'Abonnent hinzufügen',
    'subscribers.statTotal': 'Abonnenten gesamt',
    'subscribers.statTotalSub': 'Alle registrierten Empfänger',
    'subscribers.statActive': 'Aktive Abonnenten',
    'subscribers.statActiveSub': 'Bereit für Newsletter',
    'subscribers.statScheduled': 'Heute geplant',
    'subscribers.statScheduledSub': 'Nächster Versand um 06:00 CET',
    'subscribers.statIssues': 'Zustellungsprobleme',
    'subscribers.statIssuesSub': 'Erfordert Ihre Aufmerksamkeit',
    'subscribers.tableTitle': 'Abonnenten',
    'subscribers.loadingDirectory': 'Verzeichnis wird geladen…',
    'subscribers.shownOfTotal': '{filtered} von {total} angezeigt',
    'subscribers.searchPlaceholder': 'E-Mail oder Benutzername suchen…',
    'subscribers.filterAllLanguages': 'Alle Sprachen',
    'subscribers.filterAllTopics': 'Alle Themen',
    'subscribers.filterScheduled': 'Geplant',
    'subscribers.filterNotScheduled': 'Nicht geplant',
    'subscribers.loading': 'Abonnenten werden geladen…',
    'subscribers.colPreferences': 'Einstellungen',
    'subscribers.colDelivery': 'Zustellung',
    'subscribers.colLastSent': 'Zuletzt gesendet',
    'subscribers.topicsLabel': 'Themen:',
    'subscribers.dailyAt': 'Täglich um {time} {tz}',
    'subscribers.emailDigest': 'E-Mail-Digest',
    'subscribers.lastDeliverySuccess': '• Letzte Zustellung: Erfolg',
    'subscribers.notScheduled': 'Nicht geplant',
    'subscribers.lastDeliveryNa': '• Letzte Zustellung: k. A.',
    'subscribers.importTopicsTitle': 'Themen & Schlagwörter importieren (.xlsx/.csv)',
    'subscribers.importTopicsAria': 'Themen und Schlagwörter importieren',
    'subscribers.emptyNone': 'Noch keine Abonnenten. Fügen Sie einen hinzu.',
    'subscribers.emptyFilters': 'Keine Abonnenten entsprechen den Filtern.',
    'subscribers.showingSubscribers': '{from}–{to} von {total} Abonnenten',
    'subscribers.perPage': '{n} / Seite',
    'subscribers.sidebarDetails': 'Abonnentendetails',
    'subscribers.memberSince': 'Mitglied seit',
    'subscribers.topicsSectionTitle': 'Themen, Schlagwörter & Schlüsselwörter',
    'subscribers.topicsSectionDesc': 'Schlagwörter für Newsletter-Inhalt. Schlüsselwörter matchen Artikel im Scraper-Pool.',
    'subscribers.fullEditor': 'Vollständiger Editor',
    'subscribers.noTopicsHint': 'Noch keine Themen. Fügen Sie unten ein Thema hinzu.',
    'subscribers.noBuzzwordsYet': 'Noch keine Schlagwörter',
    'subscribers.noKeywordsYet': 'Noch keine Schlüsselwörter',
    'subscribers.addBuzzwordPlaceholder': 'Schlagwort hinzufügen…',
    'subscribers.addKeywordPlaceholder': 'Schlüsselwort hinzufügen…',
    'subscribers.addTopic': 'Thema hinzufügen',
    'subscribers.topicNamePlaceholder': 'Themenname…',
    'subscribers.removeBuzzwordAria': 'Schlagwort {word} entfernen',
    'subscribers.removeKeywordAria': 'Schlüsselwort {word} entfernen',
    'subscribers.deliverySettings': 'Zustellungseinstellungen',
    'subscribers.sendTime': 'Sendezeit',
    'subscribers.emailTemplate': 'E-Mail-Vorlage',
    'subscribers.defaultDigest': 'Standard-Digest',
    'subscribers.deliveryHistory': 'Versandverlauf',
    'subscribers.lastSent': 'Zuletzt gesendet',
    'subscribers.lastStatus': 'Letzter Status',
    'subscribers.lastError': 'Letzter Fehler',
    'subscribers.sendTestEmail': 'Test-E-Mail senden',
    'subscribers.toastTestEmailSoon': 'Test-E-Mail demnächst verfügbar.',
    'subscribers.pauseSubscriber': 'Abonnent pausieren',
    'subscribers.toastPauseSoon': 'Pausieren demnächst verfügbar.',
    'subscribers.deleteSubscriber': 'Abonnent löschen',
    'subscribers.editTitle': 'Abonnent bearbeiten',
    'subscribers.emailLanguage': 'E-Mail-Sprache',
    'subscribers.newPasswordOptional': 'Neues Passwort (optional)',
    'subscribers.passwordKeepCurrent': 'Leer lassen, um beizubehalten',
    'subscribers.passwordMinHint': 'Mindestens 8 Zeichen, wenn gesetzt.',
    'subscribers.deleteTitle': 'Abonnent entfernen',
    'subscribers.deleteDesc': 'Möchten Sie {email} wirklich entfernen? Dies kann nicht rückgängig gemacht werden.',
    'subscribers.addTitle': 'Abonnent hinzufügen',
    'subscribers.emailPlaceholder': 'abonnent@beispiel.de',
    'subscribers.usernamePlaceholder': 'Anmeldename',
    'subscribers.passwordPlaceholder': 'Mindestens 8 Zeichen',
    'subscribers.toastImportNothing': 'Nichts Neues zu importieren — Themen und Schlagwörter sind aktuell.',
    'subscribers.toastImportSuccess': '{created} neue Themen importiert, {merged} zusammengeführt, {buzz} Schlagwörter hinzugefügt.',
    'subscribers.toastBuzzwordAdded': 'Schlagwort hinzugefügt.',
    'subscribers.toastBuzzwordRemoved': 'Schlagwort entfernt.',
    'subscribers.toastKeywordAdded': 'Schlüsselwort hinzugefügt.',
    'subscribers.toastKeywordRemoved': 'Schlüsselwort entfernt.',
    'subscribers.toastTopicExists': 'Thema existiert bereits.',
    'subscribers.toastTopicAdded': 'Thema hinzugefügt.',
    'subscribers.errEmailUsernameRequired': 'E-Mail und Benutzername sind erforderlich.',
    'subscribers.errPasswordMin': 'Neues Passwort muss mindestens 8 Zeichen haben.',
    'subscribers.toastUpdated': 'Abonnent aktualisiert.',
    'subscribers.toastRemoved': 'Abonnent entfernt.',
    'subscribers.errAllFieldsRequired': 'E-Mail, Benutzername und Passwort sind erforderlich.',
    'subscribers.errPasswordMinCreate': 'Passwort muss mindestens 8 Zeichen haben.',
    'subscribers.toastAdded': 'Abonnent hinzugefügt.',

    'subscriberDetail.notFound': 'Abonnent nicht gefunden.',
    'subscriberDetail.backToSubscribers': 'Zurück zu Abonnenten',
    'subscriberDetail.regenerate': 'Neu generieren',
    'subscriberDetail.sendNow': 'Jetzt senden',
    'subscriberDetail.saveChanges': 'Änderungen speichern',
    'subscriberDetail.allChangesSaved': 'Alle Änderungen gespeichert',
    'subscriberDetail.dailySendTime': 'Tägliche Sendezeit',
    'subscriberDetail.emailSentDailyAt': 'E-Mail täglich um {time}',
    'subscriberDetail.emailTemplate': 'E-Mail-Vorlage',
    'subscriberDetail.defaultOption': 'Standard ({name})',
    'subscriberDetail.usingTemplate': 'Verwendet {name}',
    'subscriberDetail.usingDefault': 'Standard: {name}',
    'subscriberDetail.newsletterPreview': 'Newsletter-Vorschau',
    'subscriberDetail.previewIframeTitle': 'Newsletter-Vorschau',
    'subscriberDetail.previewUnavailable': 'Vorschau nicht verfügbar',
    'subscriberDetail.topicsBuzzwords': 'Themen & Schlagwörter',
    'subscriberDetail.topicsDesc': 'Die KI sammelt passende Artikel aus dem Datenpool und erstellt einen personalisierten Digest.',
    'subscriberDetail.topicCount': '{count} Thema',
    'subscriberDetail.topicCountPlural': '{count} Themen',
    'subscriberDetail.importing': 'Importieren…',
    'subscriberDetail.importCsv': 'Themen & Schlagwörter aus CSV / XLSX importieren',
    'subscriberDetail.importFormat': '2-Spalten-Format: {format} — eine Zeile pro Schlagwort',
    'subscriberDetail.importFormatToken': 'Thema, Schlagwort',
    'subscriberDetail.importDropHint': 'Klicken oder .csv / .xlsx ablegen',
    'subscriberDetail.deleteTopicTitle': 'Thema löschen',
    'subscriberDetail.noArticlesMatched': 'Noch keine passenden Artikel',
    'subscriberDetail.noBuzzwordsYet': 'Noch keine Schlagwörter',
    'subscriberDetail.addBuzzwordPlaceholder': 'Schlagwort hinzufügen…',
    'subscriberDetail.addNewTopic': 'Neues Thema hinzufügen',
    'subscriberDetail.topicNamePlaceholder': 'Themenname…',
    'subscriberDetail.aiSummary': 'Die KI sammelt Artikel für {topicCount} und sendet täglich um {time} ({timezone}) einen Digest an {email}.',
    'subscriberDetail.aiSummaryTopicsOne': '{count} Thema',
    'subscriberDetail.aiSummaryTopicsMany': '{count} Themen',
    'subscriberDetail.digestInLanguage': 'KI-Digest wird generiert in {language}',
    'subscriberDetail.toastSettingsSaved': 'Abonnenteneinstellungen gespeichert.',
    'subscriberDetail.toastNewsletterSent': 'Newsletter gesendet (oder in Warteschlange) über die freigegebene Entwurfspipeline.',

    'topicMultiSelect.placeholder': 'Themen auswählen…',
    'topicMultiSelect.empty': 'Noch keine Themen. Erstellen Sie Themen im Themen-Bildschirm.',
    'topicSearchPicker.placeholder': 'Thema auswählen…',
    'topicSearchPicker.search': 'Themen suchen…',
    'topicSearchPicker.allAttached': 'Alle Themen sind bereits am Artikel.',
    'topicSearchPicker.noMatch': 'Keine passenden Themen.',

    'articleAnalysis.noArticle': 'Kein Artikel ausgewählt.',
    'articleAnalysis.backToArticles': 'Zurück zu Artikeln',
    'articleAnalysis.networkError': 'Beim Laden des Artikels ist ein Fehler aufgetreten.',
    'articleAnalysis.notFound': 'Artikel konnte nicht geladen werden. Er wurde möglicherweise entfernt.',
    'articleAnalysis.articles': 'Artikel',
    'articleAnalysis.title': 'Artikelanalyse',
    'articleAnalysis.subtitle': 'Tags, Zusammenfassung, URLs und Unternehmen dieses Artikels.',
    'articleAnalysis.dataroomSetting': 'Dataroom-Einstellungen',
    'articleAnalysis.newAnalysis': 'Neue Analyse',
    'articleAnalysis.newAnalysisSoon': 'Neue Analyse demnächst verfügbar',
    'articleAnalysis.indexed': 'Indexiert',
    'articleAnalysis.tags': 'Tags',
    'articleAnalysis.buzzwords': 'Schlagwörter',
    'articleAnalysis.summary': 'Zusammenfassung',
    'articleAnalysis.companies': 'Unternehmen',
    'articleAnalysis.noAnalysisYet': 'Noch keine Analyse',
    'articleAnalysis.noTags': 'Es wurden noch keine Tags extrahiert.',
    'articleAnalysis.noCompanies': 'Keine erwähnten Unternehmen gefunden.',
    'articleAnalysis.noUrl': 'Keine URL',
    'articleAnalysis.noSummary': 'Noch keine Analyse.',
    'articleAnalysis.addTag': 'Tag hinzufügen',
    'articleAnalysis.tagPlaceholder': 'Thema suchen und auswählen…',
    'articleAnalysis.tagAdded': 'Tag hinzugefügt',
    'articleAnalysis.tagRemoved': 'Tag entfernt',
    'articleAnalysis.removeTag': 'Tag entfernen',
    'articleAnalysis.tagDuplicate': 'Dieser Tag ist bereits am Artikel.',
    'articleAnalysis.tagEmpty': 'Bitte Tag-Namen eingeben.',
    'articleAnalysis.addCompany': 'Unternehmen hinzufügen',
    'articleAnalysis.companyPlaceholder': 'Unternehmensname eingeben…',
    'articleAnalysis.companyAdded': 'Unternehmen hinzugefügt',
    'articleAnalysis.companyDuplicate': 'Dieses Unternehmen ist bereits am Artikel.',
    'articleAnalysis.companyEmpty': 'Bitte Unternehmensnamen eingeben.',
    'articleAnalysis.articlesHome': 'Artikel-Start',

    'dataPool.title': 'Datenpool',
    'dataPool.subtitle': 'Gesammelte Artikel aus allen Scraper-Quellen',
    'dataPool.searchWithTopic': 'Artikel suchen…',
    'dataPool.searchOrFilter': 'Artikel suchen oder nach Thema filtern…',
    'dataPool.filterByTopic': 'Nach Thema filtern',
    'dataPool.colTitle': 'Titel',
    'dataPool.colUrl': 'Artikel-URL',
    'dataPool.colTopic': 'Thema',
    'dataPool.moreTopics': '+{count} weitere',
    'dataPool.colTime': 'Zeit',
    'dataPool.colDataroom': 'DataRoom',
    'dataPool.alreadyInDataroom': 'Bereits im DataRoom',
    'dataPool.addToDataroom': 'Zum DataRoom hinzufügen',
    'dataPool.addToDataroomTitle': 'Diesen Artikel zum DataRoom hinzufügen',
    'dataPool.emptyDate': 'An diesem Datum keine Artikel gesammelt',
    'dataPool.emptyFilters': 'Keine Artikel entsprechen den Filtern',
    'dataPool.toastApiKey': 'Moderator-API-Schlüssel fehlt. Melden Sie sich erneut an.',
    'dataPool.toastAdded': 'Zum DataRoom hinzugefügt',
    'dataPool.toastNeedAnalysis': 'Artikel muss analysiert werden, bevor er zum DataRoom hinzugefügt wird.',
    'dataPool.toastNotFound': 'Artikel auf dem Server nicht gefunden. Datenpool aktualisieren.',

    'deliveryHistory.title': 'Versandverlauf',
    'deliveryHistory.subtitle': 'Newsletter-Versandprotokoll',
    'deliveryHistory.empty': 'Noch keine Versandprotokolle.',
    'deliveryHistory.colSentAt': 'Gesendet am',
    'deliveryHistory.logTitle': 'Protokoll #{id}',
    'deliveryHistory.deleteTitle': 'Protokoll löschen',
    'deliveryHistory.deleteConfirm': 'Protokoll löschen?',
    'deliveryHistory.deleteDesc': 'Dies kann nicht rückgängig gemacht werden.',
    'deliveryHistory.toastLoadFailed': 'Versandverlauf konnte nicht geladen werden',
    'deliveryHistory.toastDetailFailed': 'Protokolldetails konnten nicht geladen werden',
    'deliveryHistory.toastDeleted': 'Protokoll gelöscht',
    'deliveryHistory.toastDeleteFailed': 'Protokoll konnte nicht gelöscht werden',

    'draftsOutbox.title': 'Entwürfe & Postausgang',
    'draftsOutbox.subtitle': 'KI-generierte Newsletter-Entwürfe vor dem Versand prüfen',
    'draftsOutbox.tabPending': 'Ausstehende Prüfung',
    'draftsOutbox.tabAll': 'Alle Entwürfe',
    'draftsOutbox.colSubject': 'Betreff',
    'draftsOutbox.colCreated': 'Erstellt',
    'draftsOutbox.empty': 'Keine Entwürfe gefunden.',
    'draftsOutbox.draftTitle': 'Entwurf #{id}',
    'draftsOutbox.previewTitle': 'Entwurfsvorschau',
    'draftsOutbox.rejectConfirm': 'Entwurf ablehnen?',
    'draftsOutbox.rejectDesc': 'Dieser Entwurf wird als abgelehnt markiert und nicht gesendet.',
    'draftsOutbox.loadFailed': 'Entwurf konnte nicht geladen werden',
    'draftsOutbox.toastPendingFailed': 'Ausstehende Entwürfe konnten nicht geladen werden',
    'draftsOutbox.toastLoadFailed': 'Entwürfe konnten nicht geladen werden',
    'draftsOutbox.toastSent': 'Entwurf erfolgreich gesendet',
    'draftsOutbox.toastSendFailed': 'Entwurf konnte nicht gesendet werden',
    'draftsOutbox.toastRejected': 'Entwurf abgelehnt',
    'draftsOutbox.toastRejectFailed': 'Entwurf konnte nicht abgelehnt werden',

    'scraperPool.title': 'Scraper-Pool',
    'scraperPool.subtitle': 'Ihre Website-Quellen und täglicher Scrape-Zeitplan',
    'scraperPool.runNow': 'Scraper jetzt starten',
    'scraperPool.running': 'Läuft…',
    'scraperPool.statTotal': 'URLs gesamt',
    'scraperPool.statActive': 'Aktiv',
    'scraperPool.statArticles': 'Artikel im Pool',
    'scraperPool.addSource': 'Quelle hinzufügen',
    'scraperPool.addSourceDesc': 'Website-URL zum Scraper-Pool hinzufügen',
    'scraperPool.domainLabel': 'Domain oder URL',
    'scraperPool.domainPlaceholder': 'beispiel.de',
    'scraperPool.domainHint': 'https://-Präfix wird automatisch entfernt',
    'scraperPool.addToPool': 'Zum Pool hinzufügen',
    'scraperPool.tips': 'Tipps',
    'scraperPool.dailySchedule': 'Täglicher Zeitplan',
    'scraperPool.autoScrapeTime': 'Automatische Scrape-Zeit',
    'scraperPool.runsDaily': 'Läuft jeden Tag',
    'scraperPool.autoRunsRemaining': 'Auto-Läufe heute übrig',
    'scraperPool.autoRunsLabel': 'Auto-Läufe übrig',
    'scraperPool.autoRunsResetsAt': 'Zurücksetzen um {time} täglich',
    'scraperPool.autoRunsRemainingOf': '{remaining} von {total} Auto-Läufen übrig',
    'scraperPool.autoRunsRemainingHint': 'Verbleibende automatische Scrapes heute von der Tagesgrenze',
    'scraperPool.loginToSave': 'Anmelden, um Zeitplan zu speichern',
    'scraperPool.poolStatus': 'Pool-Status',
    'scraperPool.poolStatusDesc': 'Live-Übersicht Ihres Scraper-Pools',
    'scraperPool.statusLabel': 'Status',
    'scraperPool.statusIdle': 'Bereit',
    'scraperPool.nextRun': 'Nächster Lauf',
    'scraperPool.yourSources': 'Ihre Quellen',
    'scraperPool.searchPlaceholder': 'Nach URL oder Thema suchen…',
    'scraperPool.colUrl': 'URL',
    'scraperPool.colTopics': 'Themen',
    'scraperPool.colLastRun': 'Letzter Lauf',
    'scraperPool.colResult': 'Ergebnis',
    'scraperPool.colArticles': 'Artikel',
    'scraperPool.noArticles': 'Keine Artikel',
    'scraperPool.unscraped': 'Nicht gescrapt',
    'scraperPool.emptySources': 'Noch keine Quellen. URL hinzufügen zum Starten.',
    'scraperPool.emptySearch': 'Keine Quellen entsprechen der Suche.',
    'scraperPool.scraperRunning': 'Scraper läuft…',
    'scraperPool.removeTitle': 'Diese Quelle aus dem Scraper-Pool entfernen?',
    'scraperPool.removeNote': 'Bereits gesammelte Artikel bleiben im Datenpool.',
    'scraperPool.pageOf': 'Seite {page} von {total}',
    'scraperPool.rangeOf': '{from}–{to} von {total}',
    'scraperPool.activePaused': '{active} aktiv · {paused} pausiert',
    'scraperPool.failCount': '{n}× Fehler',
    'scraperPool.toastExists': 'URL bereits im Pool',
    'scraperPool.toastAdded': 'Quelle zum Pool hinzugefügt',
    'scraperPool.toastRemoved': 'Quelle aus Pool entfernt',
    'scraperPool.toastAddFailed': 'Quelle konnte nicht hinzugefügt werden',
    'scraperPool.toastRemoveFailed': 'Quelle konnte nicht entfernt werden',
    'scraperPool.toastUpdateFailed': 'Quelle konnte nicht aktualisiert werden',
    'scraperPool.toastSaveFailed': 'Zeitplan konnte nicht gespeichert werden',
    'scraperPool.toastRunFailed': 'Scraper konnte nicht gestartet werden',
    'scraperPool.quotaExtraRemaining': 'Zusatzläufe',
    'scraperPool.quotaMaxUrls': 'Max. URLs / Lauf',
    'scraperPool.quotaMaxArticles': 'Max. Artikel / URL',
    'scraperPool.quotaLocked': 'Von Admin gesperrt',
    'scraperPool.quotaLockedMessage': 'Der Scraper ist für diesen Client deaktiviert. Bitte wenden Sie sich an den Administrator.',
    'scraperPool.quotaNoExtraRuns': 'Für den aktuellen Zeitraum sind keine Zusatzläufe mehr verfügbar.',
    'scraperPool.quotaMaxUrlLimit': 'Sie können maximal {limit} URLs pro Scraper-Lauf hinzufügen.',

    'importSources.title': 'Import yourdata',
    'importSources.subtitle': 'Dokumente oder Excel-URL-Listen in den Datenpool laden',
    'importSources.tabDocument': 'Dokument',
    'importSources.tabExcel': 'URL-Liste',
    'importSources.docLabel': 'PDF- oder Word-Dokument',
    'importSources.docHint': 'Text wird extrahiert und dem Datenpool hinzugefügt',
    'importSources.uploadDoc': 'Hochladen & extrahieren',
    'importSources.charsExtracted': '{n} Zeichen extrahiert',
    'importSources.excelLabel': 'Excel-Datei (.xlsx)',
    'importSources.excelHint': 'Vollständige Artikel-URLs (keine Homepages). Links in Spalte URL/Link oder irgendwo im Blatt.',
    'importSources.invalidSampleTitle': 'Validierungsprobleme',
    'importSources.uploadExcel': 'Tabelle hochladen',
    'importSources.sheetLabel': 'Blatt',
    'importSources.selectSheet': 'Blatt wählen',
    'importSources.headerRow': 'Kopfzeile',
    'importSources.preview': 'Vorschau',
    'importSources.validate': 'Validieren',
    'importSources.confirmCrawl': 'Crawl starten',
    'importSources.validCount': '{n} gültig',
    'importSources.invalidCount': '{n} ungültig',
    'importSources.duplicateCount': '{n} Duplikat',
    'importSources.crawling': 'URLs werden im Hintergrund gecrawlt…',
    'importSources.crawlFailed': 'Crawl fehlgeschlagen',
    'importSources.toastDocSuccess': 'Dokument importiert',
    'importSources.toastDocFailed': 'Dokumentimport fehlgeschlagen',
    'importSources.toastExcelUploaded': 'Tabelle hochgeladen',
    'importSources.toastExcelFailed': 'Upload fehlgeschlagen',
    'importSources.toastPreviewReady': 'Vorschau bereit',
    'importSources.toastPreviewFailed': 'Vorschau fehlgeschlagen',
    'importSources.toastValidated': 'Validierung abgeschlossen',
    'importSources.toastValidateFailed': 'Validierung fehlgeschlagen',
    'importSources.toastCrawlStarted': 'Crawl gestartet',
    'importSources.toastConfirmFailed': 'Crawl konnte nicht gestartet werden',
    'importSources.toastCrawlDone': 'Alle URLs erfolgreich gecrawlt',
    'importSources.toastCrawlPartial': 'Crawl mit Teilfehlern abgeschlossen',
    'importSources.toastCrawlFailed': 'Crawl fehlgeschlagen',

    'importSources.toastDocReady': 'Dokument bereit — zur Bestätigung importieren',
    'importSources.confirmDoc': 'Import bestätigen',
    'importSources.toastDocConfirmed': 'Dokumentenimport bestätigt',
    'importSources.toastDocConfirmFailed': 'Dokumentenimport konnte nicht bestätigt werden',

    'campaigns.strategy.notSaved': 'Noch nicht gespeichert',
    'campaigns.strategy.lastSaved': 'Zuletzt gespeichert {date}',
    'campaigns.strategy.tzLondon': '(UTC+00:00) London, Vereinigtes Königreich',
    'campaigns.strategy.tzBerlin': '(UTC+01:00) Berlin, Deutschland',
    'campaigns.strategy.tzHcm': '(GMT+7) Ho-Chi-Minh-Stadt, Vietnam',
    'campaigns.strategy.tzParis': '(UTC+01:00) Paris, Frankreich',
    'campaigns.strategy.catSaas': 'SaaS / Technologie',
    'campaigns.strategy.catB2b': 'B2B-Marketing',
    'campaigns.strategy.catFintech': 'FinTech',
    'campaigns.strategy.catHealthcare': 'Gesundheitswesen',
    'campaigns.strategy.errObjectiveRequired': 'Ziel ist erforderlich.',
    'campaigns.strategy.errAudienceRequired': 'Zielgruppe ist erforderlich.',
    'campaigns.strategy.errBrandRequired': 'Markenrichtlinien sind erforderlich.',
    'campaigns.strategy.errFrequencyRequired': 'Posting-Häufigkeit ist erforderlich.',
    'campaigns.strategy.errMaxChars': 'Maximal {n} Zeichen.',
    'campaigns.strategy.errAddOne': 'Mindestens ein {item} hinzufügen.',
    'campaigns.strategy.errMaxItems': 'Maximal {n} {item}-Einträge.',
    'campaigns.strategy.errItemMaxLen': 'Jeder {item} darf höchstens {n} Zeichen haben.',
    'campaigns.strategy.itemTone': 'Ton',
    'campaigns.strategy.itemPillar': 'Säule',
    'campaigns.strategy.itemMessage': 'Botschaft',
    'campaigns.strategy.itemHashtag': 'Hashtag',
    'campaigns.strategy.itemTime': 'Zeit',
    'campaigns.strategy.itemCompetitor': 'Wettbewerber',
    'campaigns.strategy.previewBrand': 'Markenpositionierung: {text}',
    'campaigns.strategy.previewPillars': 'Inhaltssäulen: {text}',
    'campaigns.strategy.previewTone': 'Tonfall: {text}',
    'campaigns.strategy.previewMessages': 'Kernbotschaften: {text}',
    'campaigns.strategy.previewHashtags': 'Hashtag-Plan: {text}',

    'linkedInSchedule.title': 'LinkedIn-Zeitplan',
    'linkedInSchedule.subtitle': 'Kampagnenbeiträge planen, organisieren und automatisieren.',
    'linkedInSchedule.autoSchedule': 'Auto-Planung',
    'linkedInSchedule.newCampaign': 'Neue Kampagne',
    'linkedInSchedule.newPost': 'Neuer Beitrag',
    'linkedInSchedule.allCampaigns': 'Alle Kampagnen',
    'linkedInSchedule.allStatus': 'Alle Status',
    'linkedInSchedule.allLanguages': 'Alle Sprachen',
    'linkedInSchedule.searchPlaceholder': 'Beiträge, Titel oder Kampagnen suchen…',
    'linkedInSchedule.prevDays': 'Vorherige Tage',
    'linkedInSchedule.nextDays': 'Nächste Tage',
    'linkedInSchedule.statTotalScheduled': 'Geplante Beiträge gesamt',
    'linkedInSchedule.statDraftsPending': 'Entwürfe zur Freigabe',
    'linkedInSchedule.statActiveCampaigns': 'Aktive Kampagnen',
    'linkedInSchedule.statUpcomingAutoPosts': 'Anstehende Auto-Posts',
    'linkedInSchedule.statPostingFrequency': 'Posting-Häufigkeit',
    'linkedInSchedule.statGrowth': '+12 % vs. letzte 7 Tage',
    'linkedInSchedule.statNeedsReview': 'Prüfung erforderlich',
    'linkedInSchedule.statRunningPaused': '{active} aktiv · {paused} pausiert',
    'linkedInSchedule.statNextPost': 'Nächster: Heute, 10:00',
    'linkedInSchedule.statFrequencyValue': '4,2 Beiträge/Tag',
    'linkedInSchedule.statOptimal': 'Optimal',
    'linkedInSchedule.gridCampaigns': 'Kampagnen',
    'linkedInSchedule.postsProgress': '{approved} / {total} Beiträge',
    'linkedInSchedule.dragHint': 'Beiträge per Drag & Drop neu planen',
    'linkedInSchedule.viewMonth': 'Monat',
    'linkedInSchedule.viewWeek': 'Woche',
    'linkedInSchedule.viewList': 'Liste',
    'linkedInSchedule.draft': 'Entwurf',
    'linkedInSchedule.postDetails': 'Beitragsdetails',
    'linkedInSchedule.dayDetails': '{day} Details',
    'linkedInSchedule.postTitle': 'Beitragstitel',
    'linkedInSchedule.language': 'Sprache',
    'linkedInSchedule.scheduledDateTime': 'Geplantes Datum & Uhrzeit',
    'linkedInSchedule.campaign': 'Kampagne',
    'linkedInSchedule.objectiveCta': 'Ziel / CTA',
    'linkedInSchedule.approval': 'Freigabe',
    'linkedInSchedule.schedule': 'Planen',
    'linkedInSchedule.publishNow': 'Jetzt veröffentlichen',
    'linkedInSchedule.saveDraft': 'Entwurf speichern',
    'linkedInSchedule.deletePost': 'Beitrag löschen',
    'linkedInSchedule.storedPosts': 'Gespeicherte Beiträge',
    'linkedInSchedule.draftEmpty': 'Beiträge hierher ziehen, um sie als Entwurf zu speichern.',
    'linkedInSchedule.dayPostsCount': '{count} Beitrag(e) am {day}',
    'linkedInSchedule.noPostsDay': 'Keine Beiträge für diesen Tag geplant.',
    'linkedInSchedule.hoverLocation': 'Ort',
    'linkedInSchedule.hoverRole': 'Rolle',
    'linkedInSchedule.hoverTime': 'Uhrzeit',
    'linkedInSchedule.hoverDate': 'Datum',
    'linkedInSchedule.scheduleConflict': 'Terminkonflikt zu dieser Zeit',
    'linkedInSchedule.linkedInAccount': 'LinkedIn-Konto',
    'linkedInSchedule.removeFromDraft': '{title} aus Entwurf entfernen',
    'linkedInSchedule.datePickerTitle': 'Zeitraum auswählen',
    'linkedInSchedule.chooseStartDate': 'Startdatum wählen',
    'linkedInSchedule.chooseEndDate': 'Enddatum wählen',
    'linkedInSchedule.closeDatePicker': 'Datumsauswahl schließen',
    'linkedInSchedule.prevMonth': 'Vorheriger Monat',
    'linkedInSchedule.nextMonth': 'Nächster Monat',
    'linkedInSchedule.startDate': 'Startdatum',
    'linkedInSchedule.endDate': 'Enddatum',
    'linkedInSchedule.clearDates': 'Zurücksetzen',
    'linkedInSchedule.saveDates': 'Daten speichern',
    'linkedInSchedule.approval.approved': 'Freigegeben',
    'linkedInSchedule.approval.pending': 'Ausstehend',
    'linkedInSchedule.approval.rejected': 'Abgelehnt',
    'campaigns.tab.overview': 'Übersicht',
    'campaigns.tab.content': 'Inhalt',
    'campaigns.tab.strategy': 'Strategie',
    'campaigns.tab.schedule': 'Zeitplan',
    'campaigns.tab.analytics': 'Analysen',
    'campaigns.tab.settings': 'Einstellungen',
    'campaigns.title': 'LinkedIn',
    'campaigns.newLinkedIn': 'Neues LinkedIn',
    'campaigns.searchPlaceholder': 'LinkedIn suchen…',
    'campaigns.allStatus': 'Alle Status',
    'campaigns.campaignCount': '{count} Kampagne(n)',
    'campaigns.status.active': 'Aktiv',
    'campaigns.status.completed': 'Abgeschlossen',
    'campaigns.preview': 'Vorschau',
    'campaigns.edit': 'Bearbeiten',
    'campaigns.createdOn': 'Erstellt am {date}',
    'campaigns.type.awareness': 'Bekanntheit',
    'campaigns.type.productLaunch': 'Produktlaunch',
    'campaigns.type.engagement': 'Engagement',
    'campaigns.type.content': 'Inhalt',
    'campaigns.type.event': 'Event',
    'campaigns.type.recruitment': 'Recruiting',
    'campaigns.type.thoughtLeadership': 'Thought Leadership',
    'campaigns.contentReview': 'Inhaltsprüfung',
    'campaigns.contentReviewDesc': 'Inhalte prüfen und freigeben, bevor sie geplant werden',
    'campaigns.language': 'Sprache',
    'campaigns.posts': 'Beiträge',
    'campaigns.postsInCampaign': '{count} in dieser Kampagne',
    'campaigns.addNewPost': 'Neuen Beitrag hinzufügen',
    'campaigns.postTitle': 'Beitragstitel',
    'campaigns.content': 'Inhalt',
    'campaigns.media': 'Medien',
    'campaigns.noImages': 'Keine Bilder',
    'campaigns.imageCount': '{count} Bild(er)',
    'campaigns.uploadedMedia': 'Hochgeladene Medien',
    'campaigns.callToAction': 'Call-to-Action',
    'campaigns.cta.learnMore': 'Mehr erfahren',
    'campaigns.cta.signUp': 'Registrieren',
    'campaigns.cta.contact': 'Kontakt',
    'campaigns.cta.download': 'Herunterladen',
    'campaigns.saveDraft': 'Entwurf speichern',
    'campaigns.approveSchedule': 'Freigeben & planen',
    'campaigns.noPostSelected': 'Kein Beitrag ausgewählt',
    'campaigns.addFirstPost': 'Klicken Sie auf „Neuen Beitrag hinzufügen“, um Ihren ersten Beitrag zu erstellen',
    'campaigns.comingSoon': 'Dieser Bereich kommt bald',
    'campaigns.aiStrategyAssistant': 'KI-Strategie-Assistent',
    'campaigns.aiContentAssistant': 'KI-Inhalts-Assistent',
    'campaigns.newChat': 'Neuer Chat',
    'campaigns.strategyOverview': 'Strategieübersicht',
    'campaigns.strategyOverviewDesc': 'Definieren Sie die Inhaltsstrategie für Ihre LinkedIn-Kampagne. Diese Strategie dient als Kontext für die KI-Inhaltserstellung.',
    'campaigns.fixRequired': 'Pflichtfelder korrigieren',
    'campaigns.unsavedChanges': 'Ungespeicherte Änderungen',
    'campaigns.objective': 'Kampagnenziel',
    'campaigns.targetAudience': 'Zielgruppe',
    'campaigns.category': 'Kategorie',
    'campaigns.toneOfVoice': 'Tonfall',
    'campaigns.tonePlaceholder': 'Ton hinzufügen (z. B. Professionell)',
    'campaigns.toneNotesPlaceholder': 'Optionale Tonnotizen…',
    'campaigns.brandGuidelines': 'Markenrichtlinien',
    'campaigns.competitorPlaceholder': 'Wettbewerber hinzufügen',
    'campaigns.contentPillars': 'Inhaltssäulen',
    'campaigns.addPillar': 'Säule hinzufügen',
    'campaigns.keyMessages': 'Kernbotschaften',
    'campaigns.addMessage': 'Botschaft hinzufügen',
    'campaigns.hashtagStrategy': 'Hashtag-Strategie',
    'campaigns.hashtagPlaceholder': 'Hashtag hinzufügen',
    'campaigns.hashtagNotesPlaceholder': 'Hashtag-Hinweise…',
    'campaigns.postingFrequency': 'Posting-Häufigkeit',
    'campaigns.preferredPostingTime': 'Bevorzugte Posting-Zeit',
    'campaigns.strategyDirtyHint': 'Speichern, um die Strategie für die KI-Beitragserstellung anzuwenden.',
    'campaigns.strategySynced': 'Strategie ist mit dem letzten Speicherstand synchron.',
    'campaigns.generating': 'Wird generiert…',
    'campaigns.generateStrategyAi': 'Strategie mit KI generieren',
    'campaigns.saveStrategy': 'Strategie speichern',
    'campaigns.saving': 'Speichern…',
    'campaigns.generateStrategy': 'Strategie generieren',
    'campaigns.suggestPillars': 'Inhaltssäulen vorschlagen',
    'campaigns.suggestPillarsDesc': 'KI-Vorschläge für Inhaltssäulen basierend auf Ihrem Ziel.',
    'campaigns.suggestMessages': 'Kernbotschaften vorschlagen',
    'campaigns.suggestMessagesDesc': 'Kernbotschaften generieren, die Ihre Zielgruppe ansprechen.',
    'campaigns.suggestHashtags': 'Hashtags vorschlagen',
    'campaigns.suggestHashtagsDesc': 'Relevante Hashtags für bessere Reichweite finden.',
    'campaigns.suggestTimes': 'Posting-Zeiten vorschlagen',
    'campaigns.suggestTimesDesc': 'Optimale Posting-Zeiten basierend auf Ihrer Zielgruppe.',
    'campaigns.askAnything': 'Fragen Sie mich…',
    'campaigns.addChip': 'Chip hinzufügen',
    'campaigns.addListItem': 'Listeneintrag hinzufügen',
    'campaigns.addItem': 'Eintrag hinzufügen',
    'campaigns.toastStrategySaved': 'Strategie gespeichert. Neue Beiträge nutzen diesen Kontext für die KI-Generierung.',
    'campaigns.scheduleTitle': 'LinkedIn-Zeitplan',
    'campaigns.scheduleSubtitle': 'Beiträge per Drag & Drop planen oder neu planen',
    'campaigns.prevWeek': 'Vorherige Woche',
    'campaigns.nextWeek': 'Nächste Woche',
    'campaigns.showUnscheduled': 'Ungeplante anzeigen',
    'campaigns.allDay': 'Ganztägig',
    'campaigns.conflict': 'Konflikt',
    'campaigns.postsOnCalendar': '{name} — {count} Beiträge in einem Kalender',
    'campaigns.scheduledUnscheduled': '{scheduled} geplant · {unscheduled} ungeplant',
    'campaigns.draggingPost': 'Beitrag wird gezogen…',
    'campaigns.schedulingConflicts': '{count} Planungskonflikt(e)',
    'campaigns.scheduleRules': 'Planungsregeln',
    'campaigns.scheduleRulesDesc': 'Max. {maxPerDay} Beiträge/Tag · min. {minHours} Std. Abstand · veröffentlichte Beiträge können nicht verschoben werden. Klicken Sie einen Beitrag an, um ihn unter Inhalt zu öffnen.',
    'campaigns.unscheduledPosts': 'Ungeplante Beiträge',
    'campaigns.dropToUnschedule': 'Hier ablegen, um Planung aufzuheben',
    'campaigns.noUnscheduledPosts': 'Keine ungeplanten Beiträge',
    'campaigns.errPostedCannotReschedule': 'Veröffentlichte Beiträge können nicht neu geplant werden',
    'campaigns.overview.campaignStatus': 'Kampagnenstatus',
    'campaigns.overview.objective': 'Ziel',
    'campaigns.overview.targetAudience': 'Zielgruppe',
    'campaigns.overview.category': 'Kategorie',
    'campaigns.overview.owner': 'Verantwortlich',
    'campaigns.overview.postStatusSummary': 'Beitragsstatus-Übersicht',
    'campaigns.overview.campaignProgress': 'Kampagnenfortschritt',
    'campaigns.overview.schedulePreview': 'Zeitplan-Vorschau',
    'campaigns.overview.strategySnapshot': 'Strategie-Snapshot',
    'campaigns.overview.topHashtags': 'Top-Hashtags',
    'campaigns.overview.analyticsSnapshot': 'Analyse-Snapshot',
    'campaigns.overview.lastUpdated': 'Zuletzt aktualisiert: {date}',
    'campaigns.overview.recentActivity': 'Letzte Aktivität',
    'campaigns.overview.viewProgress': 'Fortschrittsdetails anzeigen +',
    'campaigns.overview.viewSchedule': 'Vollständigen Zeitplan anzeigen +',
    'campaigns.overview.viewStrategy': 'Strategie anzeigen +',
    'campaigns.overview.viewAnalytics': 'Analysen anzeigen +',
    'campaigns.overview.viewActivity': 'Alle Aktivitäten anzeigen +',
    'campaigns.overview.openSchedule': 'Vollständigen Zeitplan öffnen',
    'campaigns.overview.impressions': 'Impressionen',
    'campaigns.overview.clicks': 'Klicks',
    'campaigns.overview.reactions': 'Reaktionen',
    'campaigns.overview.comments': 'Kommentare',
    'campaigns.overview.shares': 'Geteilt',
    'campaigns.overview.engagementRate': 'Engagement-Rate',
    'campaigns.overview.archived': 'Archiviert',
    'campaigns.overview.nextPost': 'Nächster Beitrag',
    'campaigns.overview.following': 'Danach',
    'campaigns.overview.viewAllPosts': 'Alle Beiträge anzeigen +',
    'campaigns.overview.totalPosts': 'Beiträge gesamt',
    'campaigns.overview.completionRate': 'Abschlussrate',
    'campaigns.overview.totalPostsLabel': 'Beiträge gesamt',
    'campaigns.overview.members': '{count} Mitglieder',
    'campaigns.overview.moreHashtags': '+{count} weitere',
    'campaigns.overview.daysRemaining': 'noch {count} Tage',
    'campaigns.overview.activity.postApproved': 'Beitrag {post} wurde freigegeben',
    'campaigns.overview.activity.postScheduled': 'Beitrag {post} wurde geplant',
    'campaigns.overview.activity.postPublished': 'Beitrag {post} wurde veröffentlicht',
    'campaigns.overview.activity.aiApplied': 'KI-Inhaltsvorschläge auf Beitrag {post} angewendet',
    'campaigns.overview.activity.settingsUpdated': 'Kampagneneinstellungen wurden aktualisiert',
    'campaigns.settings.general': 'Allgemeine Einstellungen',
    'campaigns.settings.generalDesc': 'Grundinformationen und Standardpräferenzen für diese Kampagne verwalten.',
    'campaigns.settings.campaignName': 'Kampagnenname',
    'campaigns.settings.defaultLanguage': 'Standardsprache',
    'campaigns.settings.timeZone': 'Zeitzone',
    'campaigns.settings.approval': 'Freigabe-Workflow',
    'campaigns.settings.approvalDesc': 'Festlegen, wer Inhalte vor der Veröffentlichung prüft und freigibt.',
    'campaigns.settings.reviewers': 'Prüfer',
    'campaigns.settings.approvers': 'Freigeber',
    'campaigns.settings.autoApproveDrafts': 'Entwürfe automatisch freigeben',
    'campaigns.settings.reminder': 'Erinnerung',
    'campaigns.settings.approvalNote': 'Entwürfe erfordern eine manuelle Freigabe.',
    'campaigns.settings.notifications': 'Benachrichtigungen',
    'campaigns.settings.notificationsDesc': 'Wählen Sie, welche Updates Sie erhalten möchten.',
    'campaigns.settings.notifyApproval': 'Freigabeanfragen',
    'campaigns.settings.notifyReminders': 'Erinnerungen an geplante Beiträge',
    'campaigns.settings.notifyPerformance': 'Leistungsbenachrichtigungen',
    'campaigns.settings.notifyStatus': 'Kampagnenstatus-Änderungen',
    'campaigns.settings.integrations': 'Integrationen',
    'campaigns.settings.integrationsDesc': 'Externe Tools und Dienste verbinden.',
    'campaigns.settings.connected': 'Verbunden',
    'campaigns.settings.manage': 'Verwalten',
    'campaigns.settings.privacy': 'Daten & Datenschutz',
    'campaigns.settings.privacyDesc': 'Datenaufbewahrung und Datenschutzeinstellungen verwalten.',
    'campaigns.settings.dataRetention': 'Datenaufbewahrung',
    'campaigns.settings.anonymize': 'Analysedaten anonymisieren',
    'campaigns.settings.anonymizeDesc': 'Personenbezogene Daten entfernen.',
    'campaigns.settings.dangerZone': 'Gefahrenzone',
    'campaigns.settings.dangerDesc': 'Unwiderrufliche und sensible Aktionen.',
    'campaigns.settings.archiveCampaign': 'Kampagne archivieren',
    'campaigns.settings.archiveDesc': 'Archiviert die Kampagne und alle zugehörigen Inhalte.',
    'campaigns.settings.archive': 'Archivieren',
    'campaigns.settings.deleteCampaign': 'Kampagne löschen',
    'campaigns.settings.deleteDesc': 'Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten werden dauerhaft gelöscht.',
    'campaigns.settings.saveChanges': 'Änderungen speichern',
    'campaigns.settings.saving': 'Speichern…',
    'campaigns.datePicker.chooseStart': 'Startdatum wählen',
    'campaigns.datePicker.chooseEnd': 'Enddatum wählen',
    'campaigns.datePicker.reference': 'Referenz aus LinkedIn-Zeitplan',
    'campaigns.datePicker.close': 'Datumsauswahl schließen',
    'campaigns.datePicker.prevMonth': 'Vorheriger Monat',
    'campaigns.datePicker.nextMonth': 'Nächster Monat',

    'linkedInSchedule.status.scheduled': 'Geplant',
    'linkedInSchedule.status.draft': 'Entwurf',
    'linkedInSchedule.status.approved': 'Freigegeben',
    'linkedInSchedule.status.posted': 'Veröffentlicht',

    'dataroom.selectedArticles': 'Ausgewählte Artikel ({count})',
    'dataroom.selectionHint': 'Artikel im DataRoom anklicken oder hierher ziehen.',
    'dataroom.noArticlesSelected': 'Noch keine Artikel ausgewählt.',
    'dataroom.deselectArticle': 'Artikel abwählen',
    'dataroom.breadcrumbDataRooms': 'DataRooms',
    'dataroom.pageTitle': 'Dataroom-Einstellungen',
    'dataroom.libraryAriaLabel': 'Dataroom-Artikel',
    'dataroom.deliveryPublishingTitle': 'Versand & Veröffentlichung',
    'dataroom.deliveryPublishingSubtitle': 'Konfigurieren Sie, wie dieser DataRoom-Digest erstellt, geprüft und zugestellt wird.',
    'dataroom.dataroomTitle': 'DataRoom',
    'dataroom.savedArticles': '{count} gespeicherter Artikel',
    'dataroom.savedArticlesPlural': '{count} gespeicherte Artikel',
    'dataroom.searchDataroom': 'DataRoom durchsuchen…',
    'dataroom.searchNoResults': 'Keine Artikel entsprechen der Suche.',
    'dataroom.collectedTopicsAria': 'Gesammelte Themen',
    'dataroom.noTopicsYet': 'Noch keine Themen',
    'dataroom.collectedArticlesEmpty': 'Gesammelte Artikel erscheinen hier.',
    'dataroom.removeArticleFromDataroom': '{title} aus DataRoom entfernen',
    'dataroom.general': 'Allgemein',
    'dataroom.sendNow': 'Jetzt senden',
    'dataroom.scheduleSend': 'Versand planen',
    'dataroom.exportFile': 'Datei exportieren',
    'dataroom.sendNowDesc': 'Digest sofort generieren und senden.',
    'dataroom.scheduleSendDesc': 'Einmalig oder wiederholt zu einer festgelegten Zeit senden.',
    'dataroom.exportFileDesc': 'Digest als PDF, DOCX oder CSV herunterladen.',
    'dataroom.deliveryMethodAria': 'Versandmethode',
    'dataroom.saveDraft': 'Entwurf speichern',
    'dataroom.sendEmailNow': 'E-Mail jetzt senden',
    'dataroom.downloadFile': 'Datei herunterladen',
    'dataroom.working': 'Wird ausgeführt…',
    'dataroom.configuration': 'Konfiguration',
    'dataroom.recipients': 'Empfänger',
    'dataroom.recipientsSelected': '{count} Empfänger ausgewählt',
    'dataroom.recipientsSelectedPlural': '{count} Empfänger ausgewählt',
    'dataroom.searchSubscriberPlaceholder': 'Abonnent nach Name oder E-Mail suchen…',
    'dataroom.searchByNameOrEmail': 'Nach Name oder E-Mail suchen…',
    'dataroom.selectAll': 'Alle auswählen',
    'dataroom.clear': 'Leeren',
    'dataroom.loadingSubscribers': 'Abonnenten werden geladen…',
    'dataroom.noSubscribersFound': 'Keine Abonnenten gefunden.',
    'dataroom.selectedRecipients': 'Ausgewählte Empfänger ({count})',
    'dataroom.removeRecipient': '{email} entfernen',
    'dataroom.emailSubject': 'E-Mail-Betreff',
    'dataroom.sendNowInfo': 'Dieser Digest wird sofort nach der Vorschau und dem Klick auf „E-Mail jetzt senden“ versendet.',
    'dataroom.scheduleType': 'Planungstyp',
    'dataroom.oneTime': 'Einmalig',
    'dataroom.recurring': 'Wiederkehrend',
    'dataroom.date': 'Datum',
    'dataroom.time': 'Uhrzeit',
    'dataroom.timezone': 'Zeitzone',
    'dataroom.recurringInfo': 'Wiederkehrende Zeitpläne wiederholen sich täglich zur gewählten Uhrzeit (Backend-Wiederholungsjob).',
    'dataroom.exportFormat': 'Exportformat',
    'dataroom.fileContent': 'Dateiinhalt',
    'dataroom.includeArticleSummary': 'Artikelzusammenfassung einbeziehen',
    'dataroom.includeSourceLinks': 'Quelllinks einbeziehen',
    'dataroom.includeTopicGrouping': 'Themengruppierung einbeziehen',
    'dataroom.includeRawUrlsOnly': 'Nur rohe Artikel-URLs einbeziehen',
    'dataroom.fileName': 'Dateiname',
    'dataroom.previewValidation': 'Vorschau & Validierung',
    'dataroom.generate': 'Generieren',
    'dataroom.generating': 'Wird generiert…',
    'dataroom.previewGenerated': 'Vorschau erstellt',
    'dataroom.regenerate': 'Neu generieren',
    'dataroom.regenerating': 'Wird neu generiert…',
    'dataroom.generatingPreviewAi': 'Vorschau wird mit KI generiert…',
    'dataroom.regeneratingPreview': 'Vorschau wird neu generiert…',
    'dataroom.generatedAt': 'Erstellt am',
    'dataroom.articlesIncluded': 'Enthaltene Artikel',
    'dataroom.articlesCount': '{count} Artikel',
    'dataroom.topics': 'Themen',
    'dataroom.recipientsCount': '{count} Empfänger',
    'dataroom.reviewBeforeSend': 'Bitte prüfen Sie die Vorschau vor dem Versand.',
    'dataroom.subject': 'Betreff',
    'dataroom.openFullPreview': 'Vollständige Vorschau öffnen',
    'dataroom.fullPreviewAria': 'Vollständige Vorschau',
    'dataroom.previewDeviceSizeAria': 'Vorschau-Gerätegröße',
    'dataroom.fullDigestPreview': 'Vollständige Digest-Vorschau – {device}',
    'dataroom.selectDate': 'Datum wählen',
    'dataroom.selectTime': 'Uhrzeit wählen',
    'dataroom.previousMonth': 'Vorheriger Monat',
    'dataroom.nextMonth': 'Nächster Monat',
    'dataroom.weekdaySu': 'So',
    'dataroom.weekdayMo': 'Mo',
    'dataroom.weekdayTu': 'Di',
    'dataroom.weekdayWe': 'Mi',
    'dataroom.weekdayTh': 'Do',
    'dataroom.weekdayFr': 'Fr',
    'dataroom.weekdaySa': 'Sa',
    'dataroom.hour': 'Stunde',
    'dataroom.minute': 'Minute',
    'dataroom.now': 'Jetzt',
    'dataroom.am': 'AM',
    'dataroom.pm': 'PM',
    'dataroom.deviceIphone': 'iPhone',
    'dataroom.deviceAndroid': 'Android',
    'dataroom.devicePc': 'PC',
    'dataroom.assistantAriaLabel': 'KI-Inhaltsassistent',
    'dataroom.aiContentAssistant': 'KI-Inhaltsassistent',
    'dataroom.newChat': 'Neuer Chat',
    'dataroom.thinking': 'Denke nach…',
    'dataroom.messagePlaceholder': 'Nachricht eingeben…',
    'dataroom.messageAriaLabel': 'KI-Assistent-Nachricht',
    'dataroom.sendMessageAria': 'Nachricht senden',
    'dataroom.assistantWelcome': 'Hallo! Ziehen Sie Artikel aus dem DataRoom in diesen Chat, um sie als Dokumente zu importieren, und stellen Sie dann Fragen dazu. Sie können auch zu Versandeinstellungen oder Vorschau-Bereitschaft fragen.',
    'dataroom.assistantRequestFailed': 'Assistentenanfrage fehlgeschlagen. Bitte erneut versuchen.',
    'dataroom.assistantNoArticles': 'Fügen Sie mindestens einen Artikel zum DataRoom hinzu, bevor Sie den Assistenten fragen.',
    'dataroom.assistantDropHint': 'Ziehen Sie einen DataRoom-Artikel hierher, um ihn in diesen Chat zu importieren.',
    'dataroom.assistantAttachedAria': 'In diesen Chat importierte Artikel',
    'dataroom.assistantImporting': 'Artikel wird importiert…',
    'dataroom.assistantImportedArticle': '„{title}“ importiert ({chunks} Chunks). Fragen Sie mich gerne dazu.',
    'dataroom.assistantImportSuccess': '„{title}“ wurde in den Chat importiert.',
    'dataroom.assistantImportFailed': 'Artikel konnte nicht in den Chat importiert werden.',
    'dataroom.assistantAlreadyImported': '„{title}“ ist in diesem Chat bereits importiert.',
    'dataroom.assistantDropUnknownArticle': 'Dieser Artikel ist nicht mehr im DataRoom.',
    'dataroom.validationNoArticles': 'Fügen Sie mindestens einen Artikel zum DataRoom hinzu, bevor Sie eine Vorschau generieren.',
    'dataroom.validationNoSelected': 'Wählen oder ziehen Sie mindestens einen Artikel in Vorschau & Validierung.',
    'dataroom.validationGeneratePreview': 'Generieren Sie eine Vorschau vor Versand, Planung oder Export.',
    'dataroom.validationScheduleDateTime': 'Wählen Sie Datum und Uhrzeit vor der Planung.',
    'dataroom.defaultEmailSubject': 'Täglicher HAWK-Digest - {date}',
    'dataroom.toastSelectRecipient': 'Wählen Sie mindestens einen Empfänger.',
    'dataroom.toastSelectArticle': 'Wählen Sie mindestens einen Artikel für den Versand.',
    'dataroom.toastGeneratePreview': 'Generieren Sie eine Vorschau vor dem Versand.',
    'dataroom.toastGenerateBeforeSchedule': 'Generieren Sie eine Vorschau vor der Planung.',
    'dataroom.toastApiKeyMissing': 'Moderator-API-Schlüssel fehlt. Abmelden und erneut als Moderator anmelden.',
    'dataroom.toastStaleArticlesRemoved': '{count} veraltete(r) Artikel aus DataRoom entfernt ({titles}). Erneut aus dem Datenpool hinzufügen.',
    'dataroom.toastSentSuccess': 'An {count} Abonnent gesendet',
    'dataroom.toastSentSuccessPlural': 'An {count} Abonnenten gesendet',
    'dataroom.toastSentPartial': 'An {success} gesendet, {failed} fehlgeschlagen. {error}',
    'dataroom.toastSendFailed': 'Versand fehlgeschlagen. Bitte erneut versuchen.',
    'dataroom.toastChooseSchedule': 'Wählen Sie Datum und Uhrzeit.',
    'dataroom.toastScheduledSuccess': 'Geplant für {count} Abonnent',
    'dataroom.toastScheduledSuccessPlural': 'Geplant für {count} Abonnenten',
    'dataroom.toastScheduledPartial': 'Geplant für {success}, fehlgeschlagen für {failed}. {error}',
    'dataroom.toastScheduleFailed': 'Planung fehlgeschlagen. Bitte erneut versuchen.',
    'dataroom.toastCsvDownloaded': 'CSV-Datei heruntergeladen.',
    'dataroom.toastPdfPrint': 'Druckdialog für PDF-Export geöffnet.',
    'dataroom.toastDocDownloaded': 'Dokument heruntergeladen (HTML-basiertes .doc).',
    'dataroom.toastExportFailed': 'Export fehlgeschlagen. Bitte erneut versuchen.',
    'dataroom.toastDraftSaved': 'Versandeinstellungen als Entwurf gespeichert.',
    'dataroom.toastDraftSaveFailed': 'Entwurf konnte nicht gespeichert werden.',
    'dataroom.errGenerationFailed': 'Generierung fehlgeschlagen. Bitte erneut versuchen.',
    'dataroom.errStaleArticles': '{count} Artikel nicht mehr auf dem Server ({titles}). Entfernt — erneut aus Datenpool hinzufügen, dann Vorschau neu generieren.',
    'dataroom.errNoValidArticles': 'Keine gültigen Artikel zum Senden. Artikel erneut aus Datenpool hinzufügen.',
    'dataroom.errArticleNotAnalyzed': '„{title}" wurde noch nicht analysiert. Analyse im Datenpool vor dem Versand ausführen.',
    'dataroom.errSyncArticle': '„{title}" (ID {id}) konnte nicht synchronisiert werden: {reason}',
    'dataroom.errDispatchNoHistory': 'Versand hat keine Verlaufs-ID zurückgegeben.',
    'dataroom.errEmailDeliveryFailed': 'E-Mail-Zustellung auf dem Server fehlgeschlagen (SMTP).',
    'dataroom.errDispatchStatus': 'Versand abgeschlossen mit Status „{status}".',
  },

  vi: {
    'common.cancel': 'Hủy',
    'common.save': 'Lưu',
    'common.delete': 'Xóa',
    'common.close': 'Đóng',
    'common.loading': 'Đang tải',
    'common.retry': 'Thử lại',
    'common.all': 'Tất cả',
    'common.previous': 'Trước',
    'common.next': 'Sau',
    'common.today': 'Hôm nay',
    'common.tomorrow': 'Ngày mai',
    'common.yesterday': 'Hôm qua',
    'common.subscriber': 'Người đăng ký',
    'common.template': 'Mẫu',
    'common.status': 'Trạng thái',
    'common.actions': 'Thao tác',
    'common.open': 'Mở',
    'common.search': 'Tìm kiếm',
    'common.clearFilters': 'Xóa bộ lọc',
    'common.never': 'Chưa bao giờ',
    'common.justNow': 'Vừa xong',
    'common.minAgo': '{n} phút trước',
    'common.hoursAgo': '{n} giờ trước',
    'common.daysAgo': '{n} ngày trước',
    'common.unknownTime': 'không rõ thời gian',
    'common.secondsAgo': '{n}s trước',
    'common.minutesAgoShort': '{n}p trước',
    'common.hoursAgoShort': '{n}g trước',
    'common.daysAgoShort': '{n}n trước',
    'common.articles': '{count} bài',
    'common.articlesOf': '{shown}/{total} bài',
    'common.articlesToday': '{count} bài hôm nay',
    'common.optional': '(tùy chọn)',
    'common.reject': 'Từ chối',
    'common.send': 'Gửi',
    'common.remove': 'Gỡ',
    'common.saved': 'Đã lưu',
    'common.dismiss': 'Đóng',
    'common.off': 'Tắt',
    'common.paused': 'Tạm dừng',
    'common.success': 'Thành công',
    'common.failed': 'Thất bại',
    'common.uncategorized': 'Chưa phân loại',
    'common.articleNumber': 'Bài #{id}',
    'common.create': 'Tạo',
    'common.edit': 'Sửa',
    'common.reset': 'Đặt lại',
    'common.email': 'Email',
    'common.username': 'Tên đăng nhập',
    'common.language': 'Ngôn ngữ',
    'common.password': 'Mật khẩu',
    'common.saving': 'Đang lưu…',
    'common.creating': 'Đang tạo…',
    'common.adding': 'Đang thêm…',
    'common.removing': 'Đang gỡ…',
    'common.active': 'Hoạt động',
    'common.inactive': 'Không hoạt động',
    'common.system': 'Hệ thống',
    'common.custom': 'Tùy chỉnh',
    'common.default': 'Mặc định',
    'common.topics': 'Chủ đề',
    'common.buzzwords': 'Từ khóa nổi bật',
    'common.keywords': 'Từ khóa',
    'common.preview': 'Xem trước',
    'common.duplicate': 'Nhân bản',
    'common.description': 'Mô tả',
    'common.frequency': 'Tần suất',
    'common.timezone': 'Múi giờ',
    'common.profile': 'Hồ sơ',
    'common.memberSince': 'Thành viên từ',
    'common.daily': 'Hằng ngày',
    'common.topic': 'Chủ đề',
    'common.schedule': 'Lịch gửi',
    'common.regenerate': 'Tạo lại',
    'common.showingRange': 'Hiển thị {from}–{to} / {total}',

    'emailTemplates.title': 'Mẫu email',
    'emailTemplates.subtitle': 'Chọn, xem trước, tùy chỉnh và gán mẫu email cho người đăng ký.',
    'emailTemplates.importTemplate': 'Nhập mẫu',
    'emailTemplates.newCustomTemplate': 'Mẫu tùy chỉnh mới',
    'emailTemplates.toastImportSoon': 'Tính năng nhập sắp ra mắt.',
    'emailTemplates.loadErrorTitle': 'Không tải được mẫu từ API',
    'emailTemplates.toastLoadTemplates': 'Không tải được mẫu email: {msg}',
    'emailTemplates.toastLoadSubscribers': 'Không tải được người đăng ký: {msg}',
    'emailTemplates.statTotal': 'Tổng mẫu',
    'emailTemplates.statTotalSub': 'Tất cả mẫu email',
    'emailTemplates.statSystem': 'Mẫu hệ thống',
    'emailTemplates.statSystemSub': 'Do hệ thống cung cấp',
    'emailTemplates.statCustom': 'Mẫu tùy chỉnh',
    'emailTemplates.statCustomSub': 'Do bạn tạo',
    'emailTemplates.statAssigned': 'Người đăng ký đã gán',
    'emailTemplates.statAssignedSub': 'Đã gán mẫu',
    'emailTemplates.libraryTitle': 'Thư viện mẫu',
    'emailTemplates.searchPlaceholder': 'Tìm mẫu…',
    'emailTemplates.tab.all': 'Tất cả',
    'emailTemplates.tab.system': 'Hệ thống',
    'emailTemplates.tab.custom': 'Tùy chỉnh',
    'emailTemplates.tab.shared': 'Chia sẻ',
    'emailTemplates.badge.default': 'Mặc định',
    'emailTemplates.badge.shared': '@Chia sẻ',
    'emailTemplates.usedByOne': 'Dùng bởi {count} người đăng ký',
    'emailTemplates.usedByMany': 'Dùng bởi {count} người đăng ký',
    'emailTemplates.emptyTemplates': 'Không tìm thấy mẫu.',
    'emailTemplates.showingTemplates': 'Hiển thị 1–{shown} / {total} mẫu',
    'emailTemplates.detailsTitle': 'Chi tiết & xem trước mẫu',
    'emailTemplates.usedByLabel': 'Dùng bởi',
    'emailTemplates.subscriberOne': '{count} người đăng ký',
    'emailTemplates.subscriberMany': '{count} người đăng ký',
    'emailTemplates.lastUpdated': 'Cập nhật lần cuối',
    'emailTemplates.createdBy': 'Tạo bởi',
    'emailTemplates.createdBySystem': 'Hệ thống',
    'emailTemplates.createdByYou': 'Bạn',
    'emailTemplates.actionsTitle': 'Thao tác',
    'emailTemplates.setAsDefault': 'Đặt làm mặc định',
    'emailTemplates.sendTestEmail': 'Gửi email thử',
    'emailTemplates.toastDuplicateSoon': 'Tính năng nhân bản sắp ra mắt.',
    'emailTemplates.toastTestEmailSoon': 'Tính năng gửi thử sắp ra mắt.',
    'emailTemplates.selectToViewDetails': 'Chọn mẫu để xem chi tiết',
    'emailTemplates.previewTitle': 'Xem trước',
    'emailTemplates.desktop': 'Máy tính',
    'emailTemplates.mobile': 'Di động',
    'emailTemplates.fromLabel': 'Từ: hawk@digest.io',
    'emailTemplates.viewInBrowser': 'Xem trên trình duyệt',
    'emailTemplates.previewDigestLine': '— Nội dung digest cá nhân hóa của bạn',
    'emailTemplates.previewPoweredLine': '— Powered by Hawk AI',
    'emailTemplates.moreArticles': '+ thêm bài được cá nhân hóa cho từng người đăng ký…',
    'emailTemplates.poweredBy': 'Powered by Hawk AI',
    'emailTemplates.selectToPreview': 'Chọn mẫu để xem trước',
    'emailTemplates.assignmentsTitle': 'Gán mẫu cho người đăng ký',
    'emailTemplates.assignmentsSubtitle': 'Gán mẫu cho người đăng ký. Người chưa được gán sẽ dùng mẫu mặc định.',
    'emailTemplates.searchSubscriber': 'Tìm người đăng ký…',
    'emailTemplates.filterTemplate': 'Mẫu',
    'emailTemplates.filterStatus': 'Trạng thái',
    'emailTemplates.statusSaved': 'Đã lưu',
    'emailTemplates.statusUnsaved': 'Chưa lưu',
    'emailTemplates.bulkAssign': 'Gán hàng loạt',
    'emailTemplates.toastBulkAssignSoon': 'Tính năng gán hàng loạt sắp ra mắt.',
    'emailTemplates.colAssignedTemplate': 'Mẫu đã gán',
    'emailTemplates.colTemplateType': 'Loại mẫu',
    'emailTemplates.colLastUpdated': 'Cập nhật lần cuối',
    'emailTemplates.changeTemplate': 'Đổi mẫu',
    'emailTemplates.resetToDefault': 'Đặt lại mặc định',
    'emailTemplates.emptySubscribers': 'Không tìm thấy người đăng ký.',
    'emailTemplates.showingSubscribers': 'Hiển thị 1–{shown} / {total} người đăng ký',
    'emailTemplates.dialogNewTitle': 'Mẫu tùy chỉnh mới',
    'emailTemplates.dialogEditTitle': 'Sửa mẫu',
    'emailTemplates.saveChanges': 'Lưu thay đổi',
    'emailTemplates.deleteTitle': 'Xóa mẫu?',
    'emailTemplates.deleteDesc': 'Xóa {name}? Người đăng ký dùng mẫu này sẽ chuyển về mặc định. Không thể hoàn tác.',
    'emailTemplates.changeAssignmentTitle': 'Đổi gán mẫu',
    'emailTemplates.changeAssignmentDesc': 'Gán mẫu cho {email}',
    'emailTemplates.useDefault': 'Dùng mặc định',
    'emailTemplates.defaultSuffix': '(mặc định)',
    'emailTemplates.toastCreated': 'Đã tạo mẫu.',
    'emailTemplates.toastUpdated': 'Đã cập nhật mẫu.',
    'emailTemplates.toastDefaultUpdated': 'Đã cập nhật mẫu mặc định.',
    'emailTemplates.toastDeleted': 'Đã xóa mẫu.',
    'emailTemplates.toastResetAssignment': 'Đã đặt lại mẫu mặc định.',
    'emailTemplates.toastAssignmentUpdated': 'Đã cập nhật gán mẫu.',
    'emailTemplates.formName': 'Tên mẫu',
    'emailTemplates.formNamePlaceholder': 'vd. Digest tùy chỉnh của tôi',
    'emailTemplates.formDescriptionPlaceholder': 'Mô tả ngắn về bố cục này',
    'emailTemplates.formTag': 'Thẻ',
    'emailTemplates.formTagPlaceholder': 'vd. minimal, tech, finance',
    'emailTemplates.formAccent': 'Màu nhấn',
    'emailTemplates.accent.blue': 'Xanh dương',
    'emailTemplates.accent.amber': 'Hổ phách',
    'emailTemplates.accent.cyan': 'Xanh lơ',
    'emailTemplates.accent.rose': 'Hồng',

    'subscribers.title': 'Người đăng ký',
    'subscribers.subtitle': 'Quản lý người nhận, lịch gửi và tùy chọn newsletter.',
    'subscribers.importCsv': 'Nhập CSV',
    'subscribers.toastImportCsvSoon': 'Tính năng nhập CSV sắp ra mắt.',
    'subscribers.addSubscriber': 'Thêm người đăng ký',
    'subscribers.statTotal': 'Tổng người đăng ký',
    'subscribers.statTotalSub': 'Tất cả người nhận đã đăng ký',
    'subscribers.statActive': 'Đang hoạt động',
    'subscribers.statActiveSub': 'Sẵn sàng nhận newsletter',
    'subscribers.statScheduled': 'Lên lịch hôm nay',
    'subscribers.statScheduledSub': 'Gửi tiếp lúc 06:00 CET',
    'subscribers.statIssues': 'Sự cố gửi',
    'subscribers.statIssuesSub': 'Cần bạn xử lý',
    'subscribers.tableTitle': 'Người đăng ký',
    'subscribers.loadingDirectory': 'Đang tải danh sách…',
    'subscribers.shownOfTotal': '{filtered}/{total} đang hiển thị',
    'subscribers.searchPlaceholder': 'Tìm email hoặc tên đăng nhập…',
    'subscribers.filterAllLanguages': 'Tất cả ngôn ngữ',
    'subscribers.filterAllTopics': 'Tất cả chủ đề',
    'subscribers.filterScheduled': 'Đã lên lịch',
    'subscribers.filterNotScheduled': 'Chưa lên lịch',
    'subscribers.loading': 'Đang tải người đăng ký…',
    'subscribers.colPreferences': 'Tùy chọn',
    'subscribers.colDelivery': 'Gửi',
    'subscribers.colLastSent': 'Gửi lần cuối',
    'subscribers.topicsLabel': 'Chủ đề:',
    'subscribers.dailyAt': 'Hằng ngày lúc {time} {tz}',
    'subscribers.emailDigest': 'Digest email',
    'subscribers.lastDeliverySuccess': '• Gửi lần cuối: Thành công',
    'subscribers.notScheduled': 'Chưa lên lịch',
    'subscribers.lastDeliveryNa': '• Gửi lần cuối: N/A',
    'subscribers.importTopicsTitle': 'Nhập chủ đề & từ khóa nổi bật (.xlsx/.csv)',
    'subscribers.importTopicsAria': 'Nhập chủ đề và từ khóa nổi bật',
    'subscribers.emptyNone': 'Chưa có người đăng ký. Thêm một người để bắt đầu.',
    'subscribers.emptyFilters': 'Không có người đăng ký khớp bộ lọc.',
    'subscribers.showingSubscribers': 'Hiển thị {from}–{to} / {total} người đăng ký',
    'subscribers.perPage': '{n} / trang',
    'subscribers.sidebarDetails': 'Chi tiết người đăng ký',
    'subscribers.memberSince': 'Thành viên từ',
    'subscribers.topicsSectionTitle': 'Chủ đề, từ khóa nổi bật & từ khóa',
    'subscribers.topicsSectionDesc': 'Từ khóa nổi bật cho nội dung newsletter. Từ khóa khớp bài trong pool scraper.',
    'subscribers.fullEditor': 'Trình sửa đầy đủ',
    'subscribers.noTopicsHint': 'Chưa có chủ đề. Thêm chủ đề bên dưới để gán từ khóa.',
    'subscribers.noBuzzwordsYet': 'Chưa có từ khóa nổi bật',
    'subscribers.noKeywordsYet': 'Chưa có từ khóa',
    'subscribers.addBuzzwordPlaceholder': 'Thêm từ khóa nổi bật…',
    'subscribers.addKeywordPlaceholder': 'Thêm từ khóa…',
    'subscribers.addTopic': 'Thêm chủ đề',
    'subscribers.topicNamePlaceholder': 'Tên chủ đề…',
    'subscribers.removeBuzzwordAria': 'Gỡ từ khóa nổi bật {word}',
    'subscribers.removeKeywordAria': 'Gỡ từ khóa {word}',
    'subscribers.deliverySettings': 'Cài đặt gửi',
    'subscribers.sendTime': 'Giờ gửi',
    'subscribers.emailTemplate': 'Mẫu email',
    'subscribers.defaultDigest': 'Digest mặc định',
    'subscribers.deliveryHistory': 'Lịch sử gửi',
    'subscribers.lastSent': 'Gửi lần cuối',
    'subscribers.lastStatus': 'Trạng thái lần cuối',
    'subscribers.lastError': 'Lỗi lần cuối',
    'subscribers.sendTestEmail': 'Gửi email thử',
    'subscribers.toastTestEmailSoon': 'Tính năng gửi thử sắp ra mắt.',
    'subscribers.pauseSubscriber': 'Tạm dừng người đăng ký',
    'subscribers.toastPauseSoon': 'Tính năng tạm dừng sắp ra mắt.',
    'subscribers.deleteSubscriber': 'Xóa người đăng ký',
    'subscribers.editTitle': 'Sửa người đăng ký',
    'subscribers.emailLanguage': 'Ngôn ngữ email',
    'subscribers.newPasswordOptional': 'Mật khẩu mới (tùy chọn)',
    'subscribers.passwordKeepCurrent': 'Để trống để giữ mật khẩu hiện tại',
    'subscribers.passwordMinHint': 'Tối thiểu 8 ký tự khi đặt.',
    'subscribers.deleteTitle': 'Gỡ người đăng ký',
    'subscribers.deleteDesc': 'Bạn có chắc muốn gỡ {email}? Không thể hoàn tác.',
    'subscribers.addTitle': 'Thêm người đăng ký',
    'subscribers.emailPlaceholder': 'subscriber@example.com',
    'subscribers.usernamePlaceholder': 'tên đăng nhập',
    'subscribers.passwordPlaceholder': 'Tối thiểu 8 ký tự',
    'subscribers.toastImportNothing': 'Không có gì mới để nhập — chủ đề và từ khóa đã cập nhật.',
    'subscribers.toastImportSuccess': 'Đã nhập {created} chủ đề mới, gộp {merged}, thêm {buzz} từ khóa nổi bật.',
    'subscribers.toastBuzzwordAdded': 'Đã thêm từ khóa nổi bật.',
    'subscribers.toastBuzzwordRemoved': 'Đã gỡ từ khóa nổi bật.',
    'subscribers.toastKeywordAdded': 'Đã thêm từ khóa.',
    'subscribers.toastKeywordRemoved': 'Đã gỡ từ khóa.',
    'subscribers.toastTopicExists': 'Chủ đề đã tồn tại.',
    'subscribers.toastTopicAdded': 'Đã thêm chủ đề.',
    'subscribers.errEmailUsernameRequired': 'Email và tên đăng nhập là bắt buộc.',
    'subscribers.errPasswordMin': 'Mật khẩu mới phải có ít nhất 8 ký tự.',
    'subscribers.toastUpdated': 'Đã cập nhật người đăng ký.',
    'subscribers.toastRemoved': 'Đã gỡ người đăng ký.',
    'subscribers.errAllFieldsRequired': 'Email, tên đăng nhập và mật khẩu là bắt buộc.',
    'subscribers.errPasswordMinCreate': 'Mật khẩu phải có ít nhất 8 ký tự.',
    'subscribers.toastAdded': 'Đã thêm người đăng ký.',

    'subscriberDetail.notFound': 'Không tìm thấy người đăng ký.',
    'subscriberDetail.backToSubscribers': 'Quay lại danh sách',
    'subscriberDetail.regenerate': 'Tạo lại',
    'subscriberDetail.sendNow': 'Gửi ngay',
    'subscriberDetail.saveChanges': 'Lưu thay đổi',
    'subscriberDetail.allChangesSaved': 'Đã lưu mọi thay đổi',
    'subscriberDetail.dailySendTime': 'Giờ gửi hằng ngày',
    'subscriberDetail.emailSentDailyAt': 'Email gửi hằng ngày lúc {time}',
    'subscriberDetail.emailTemplate': 'Mẫu email',
    'subscriberDetail.defaultOption': 'Mặc định ({name})',
    'subscriberDetail.usingTemplate': 'Đang dùng {name}',
    'subscriberDetail.usingDefault': 'Mặc định: {name}',
    'subscriberDetail.newsletterPreview': 'Xem trước newsletter',
    'subscriberDetail.previewIframeTitle': 'Xem trước newsletter',
    'subscriberDetail.previewUnavailable': 'Không xem trước được',
    'subscriberDetail.topicsBuzzwords': 'Chủ đề & từ khóa nổi bật',
    'subscriberDetail.topicsDesc': 'AI thu thập bài khớp các chủ đề từ kho dữ liệu và tạo digest cá nhân hóa.',
    'subscriberDetail.topicCount': '{count} chủ đề',
    'subscriberDetail.topicCountPlural': '{count} chủ đề',
    'subscriberDetail.importing': 'Đang nhập…',
    'subscriberDetail.importCsv': 'Nhập chủ đề & từ khóa nổi bật từ CSV / XLSX',
    'subscriberDetail.importFormat': 'Định dạng 2 cột: {format} — một dòng mỗi từ khóa nổi bật',
    'subscriberDetail.importFormatToken': 'chủ đề, từ khóa nổi bật',
    'subscriberDetail.importDropHint': 'Nhấp để chọn hoặc thả file .csv / .xlsx',
    'subscriberDetail.deleteTopicTitle': 'Xóa chủ đề',
    'subscriberDetail.noArticlesMatched': 'Chưa có bài khớp',
    'subscriberDetail.noBuzzwordsYet': 'Chưa có từ khóa nổi bật',
    'subscriberDetail.addBuzzwordPlaceholder': 'Thêm từ khóa nổi bật…',
    'subscriberDetail.addNewTopic': 'Thêm chủ đề mới',
    'subscriberDetail.topicNamePlaceholder': 'Tên chủ đề…',
    'subscriberDetail.aiSummary': 'AI sẽ thu bài cho {topicCount} và gửi digest tới {email} hằng ngày lúc {time} ({timezone}).',
    'subscriberDetail.aiSummaryTopicsOne': '{count} chủ đề',
    'subscriberDetail.aiSummaryTopicsMany': '{count} chủ đề',
    'subscriberDetail.digestInLanguage': 'Digest AI sẽ được tạo bằng {language}',
    'subscriberDetail.toastSettingsSaved': 'Đã lưu cài đặt người đăng ký.',
    'subscriberDetail.toastNewsletterSent': 'Newsletter đã gửi (hoặc xếp hàng) qua pipeline bản nháp đã duyệt.',

    'topicMultiSelect.placeholder': 'Chọn chủ đề…',
    'topicMultiSelect.empty': 'Chưa có chủ đề. Tạo chủ đề ở màn hình Chủ đề trước.',
    'topicSearchPicker.placeholder': 'Chọn chủ đề…',
    'topicSearchPicker.search': 'Tìm chủ đề…',
    'topicSearchPicker.allAttached': 'Tất cả chủ đề đã có trên bài viết.',
    'topicSearchPicker.noMatch': 'Không có chủ đề phù hợp.',

    'articleAnalysis.noArticle': 'Chưa chọn bài viết.',
    'articleAnalysis.backToArticles': 'Quay lại danh sách bài',
    'articleAnalysis.networkError': 'Đã xảy ra lỗi khi tải bài viết.',
    'articleAnalysis.notFound': 'Không thể tải bài viết. Có thể đã bị xóa hoặc liên kết hết hạn.',
    'articleAnalysis.articles': 'Bài viết',
    'articleAnalysis.title': 'Phân tích bài viết',
    'articleAnalysis.subtitle': 'Xem thẻ, tóm tắt, URL và công ty được trích từ bài viết này.',
    'articleAnalysis.dataroomSetting': 'Cài đặt Dataroom',
    'articleAnalysis.newAnalysis': 'Phân tích mới',
    'articleAnalysis.newAnalysisSoon': 'Tính năng phân tích mới sắp ra mắt',
    'articleAnalysis.indexed': 'Đã lập chỉ mục',
    'articleAnalysis.tags': 'Thẻ',
    'articleAnalysis.buzzwords': 'Từ khóa nổi bật',
    'articleAnalysis.summary': 'Tóm tắt',
    'articleAnalysis.companies': 'Công ty',
    'articleAnalysis.noAnalysisYet': 'Chưa có phân tích',
    'articleAnalysis.noTags': 'Chưa trích xuất thẻ nào từ bài viết này.',
    'articleAnalysis.noCompanies': 'Chưa tìm thấy công ty nào được đề cập.',
    'articleAnalysis.noUrl': 'Không có URL',
    'articleAnalysis.noSummary': 'Chưa có phân tích.',
    'articleAnalysis.addTag': 'Thêm thẻ',
    'articleAnalysis.tagPlaceholder': 'Tìm và chọn chủ đề…',
    'articleAnalysis.tagAdded': 'Đã thêm thẻ',
    'articleAnalysis.tagRemoved': 'Đã xóa thẻ',
    'articleAnalysis.removeTag': 'Xóa thẻ',
    'articleAnalysis.tagDuplicate': 'Thẻ này đã có trên bài viết.',
    'articleAnalysis.tagEmpty': 'Vui lòng nhập tên thẻ.',
    'articleAnalysis.addCompany': 'Thêm công ty',
    'articleAnalysis.companyPlaceholder': 'Nhập tên công ty…',
    'articleAnalysis.companyAdded': 'Đã thêm công ty',
    'articleAnalysis.companyDuplicate': 'Công ty này đã có trên bài viết.',
    'articleAnalysis.companyEmpty': 'Vui lòng nhập tên công ty.',
    'articleAnalysis.articlesHome': 'Trang bài viết',

    'dataPool.title': 'Kho dữ liệu',
    'dataPool.subtitle': 'Bài viết thu thập từ mọi nguồn scraper',
    'dataPool.searchWithTopic': 'Tìm bài viết…',
    'dataPool.searchOrFilter': 'Tìm bài hoặc lọc theo chủ đề…',
    'dataPool.filterByTopic': 'Lọc theo chủ đề',
    'dataPool.colTitle': 'Tiêu đề',
    'dataPool.colUrl': 'URL bài viết',
    'dataPool.colTopic': 'Chủ đề',
    'dataPool.moreTopics': '+{count} nữa',
    'dataPool.colTime': 'Thời gian',
    'dataPool.colDataroom': 'DataRoom',
    'dataPool.alreadyInDataroom': 'Đã có trong DataRoom',
    'dataPool.addToDataroom': 'Thêm vào DataRoom',
    'dataPool.addToDataroomTitle': 'Thêm bài này vào DataRoom',
    'dataPool.emptyDate': 'Không có bài nào trong ngày này',
    'dataPool.emptyFilters': 'Không có bài phù hợp bộ lọc',
    'dataPool.toastApiKey': 'Thiếu khóa API moderator. Đăng xuất và đăng nhập lại.',
    'dataPool.toastAdded': 'Đã thêm vào DataRoom',
    'dataPool.toastNeedAnalysis': 'Bài phải được phân tích trước khi thêm vào DataRoom.',
    'dataPool.toastNotFound': 'Không tìm thấy bài trên máy chủ. Làm mới Kho dữ liệu.',

    'deliveryHistory.title': 'Lịch sử gửi',
    'deliveryHistory.subtitle': 'Nhật ký gửi newsletter',
    'deliveryHistory.empty': 'Chưa có nhật ký gửi.',
    'deliveryHistory.colSentAt': 'Gửi lúc',
    'deliveryHistory.logTitle': 'Nhật ký #{id}',
    'deliveryHistory.deleteTitle': 'Xóa nhật ký',
    'deliveryHistory.deleteConfirm': 'Xóa nhật ký?',
    'deliveryHistory.deleteDesc': 'Không thể hoàn tác.',
    'deliveryHistory.toastLoadFailed': 'Không tải được lịch sử gửi',
    'deliveryHistory.toastDetailFailed': 'Không tải được chi tiết nhật ký',
    'deliveryHistory.toastDeleted': 'Đã xóa nhật ký',
    'deliveryHistory.toastDeleteFailed': 'Không xóa được nhật ký',

    'draftsOutbox.title': 'Bản nháp & Hộp thư đi',
    'draftsOutbox.subtitle': 'Xem lại bản nháp newsletter do AI tạo trước khi gửi',
    'draftsOutbox.tabPending': 'Chờ duyệt',
    'draftsOutbox.tabAll': 'Tất cả bản nháp',
    'draftsOutbox.colSubject': 'Tiêu đề',
    'draftsOutbox.colCreated': 'Tạo lúc',
    'draftsOutbox.empty': 'Không có bản nháp.',
    'draftsOutbox.draftTitle': 'Bản nháp #{id}',
    'draftsOutbox.previewTitle': 'Xem trước bản nháp',
    'draftsOutbox.rejectConfirm': 'Từ chối bản nháp?',
    'draftsOutbox.rejectDesc': 'Bản nháp sẽ bị đánh dấu từ chối và không được gửi.',
    'draftsOutbox.loadFailed': 'Không tải được bản nháp',
    'draftsOutbox.toastPendingFailed': 'Không tải được bản nháp chờ duyệt',
    'draftsOutbox.toastLoadFailed': 'Không tải được bản nháp',
    'draftsOutbox.toastSent': 'Đã gửi bản nháp thành công',
    'draftsOutbox.toastSendFailed': 'Không gửi được bản nháp',
    'draftsOutbox.toastRejected': 'Đã từ chối bản nháp',
    'draftsOutbox.toastRejectFailed': 'Không từ chối được bản nháp',

    'scraperPool.title': 'Nguồn scraper',
    'scraperPool.subtitle': 'Nguồn website và lịch thu thập hằng ngày',
    'scraperPool.runNow': 'Chạy scraper ngay',
    'scraperPool.running': 'Đang chạy…',
    'scraperPool.statTotal': 'Tổng URL',
    'scraperPool.statActive': 'Đang hoạt động',
    'scraperPool.statArticles': 'Bài trong kho',
    'scraperPool.addSource': 'Thêm nguồn',
    'scraperPool.addSourceDesc': 'Thêm URL website vào pool scraper',
    'scraperPool.domainLabel': 'Tên miền hoặc URL',
    'scraperPool.domainPlaceholder': 'example.com',
    'scraperPool.domainHint': 'Tiền tố https:// sẽ được bỏ tự động',
    'scraperPool.addToPool': 'Thêm vào pool',
    'scraperPool.tips': 'Mẹo',
    'scraperPool.dailySchedule': 'Lịch hằng ngày',
    'scraperPool.autoScrapeTime': 'Giờ thu thập tự động',
    'scraperPool.runsDaily': 'Chạy mỗi ngày',
    'scraperPool.autoRunsRemaining': 'lượt auto còn hôm nay',
    'scraperPool.autoRunsLabel': 'Lượt auto còn lại',
    'scraperPool.autoRunsResetsAt': 'Reset lúc {time} mỗi ngày',
    'scraperPool.autoRunsRemainingOf': 'Còn {remaining}/{total} lượt auto',
    'scraperPool.autoRunsRemainingHint': 'Số lần cào tự động còn lại trong ngày trên tổng hạn mức',
    'scraperPool.loginToSave': 'Đăng nhập để lưu lịch',
    'scraperPool.poolStatus': 'Trạng thái pool',
    'scraperPool.poolStatusDesc': 'Tổng quan scraper pool của bạn',
    'scraperPool.statusLabel': 'Trạng thái',
    'scraperPool.statusIdle': 'Đang chờ',
    'scraperPool.nextRun': 'Lần chạy tiếp',
    'scraperPool.yourSources': 'Nguồn của bạn',
    'scraperPool.searchPlaceholder': 'Tìm theo URL hoặc chủ đề…',
    'scraperPool.colUrl': 'URL',
    'scraperPool.colTopics': 'Chủ đề',
    'scraperPool.colLastRun': 'Lần chạy cuối',
    'scraperPool.colResult': 'Kết quả',
    'scraperPool.colArticles': 'Bài viết',
    'scraperPool.noArticles': 'Không có bài',
    'scraperPool.unscraped': 'Chưa thu thập',
    'scraperPool.emptySources': 'Chưa có nguồn. Thêm URL để bắt đầu.',
    'scraperPool.emptySearch': 'Không có nguồn khớp tìm kiếm.',
    'scraperPool.scraperRunning': 'Scraper đang chạy…',
    'scraperPool.removeTitle': 'Gỡ nguồn này khỏi pool scraper?',
    'scraperPool.removeNote': 'Bài đã thu vẫn giữ trong kho dữ liệu.',
    'scraperPool.pageOf': 'Trang {page}/{total}',
    'scraperPool.rangeOf': '{from}–{to} / {total}',
    'scraperPool.activePaused': '{active} hoạt động · {paused} tạm dừng',
    'scraperPool.failCount': '{n}× lỗi',
    'scraperPool.toastExists': 'URL đã có trong pool',
    'scraperPool.toastAdded': 'Đã thêm nguồn vào pool',
    'scraperPool.toastRemoved': 'Đã gỡ nguồn khỏi pool',
    'scraperPool.toastAddFailed': 'Không thêm được nguồn',
    'scraperPool.toastRemoveFailed': 'Không gỡ được nguồn',
    'scraperPool.toastUpdateFailed': 'Không cập nhật được nguồn',
    'scraperPool.toastSaveFailed': 'Không lưu được lịch',
    'scraperPool.toastRunFailed': 'Không chạy được scraper',
    'scraperPool.quotaExtraRemaining': 'Lượt chạy thêm',
    'scraperPool.quotaMaxUrls': 'Số URL tối đa / lượt chạy',
    'scraperPool.quotaMaxArticles': 'Số bài tối đa / URL',
    'scraperPool.quotaLocked': 'Bị quản trị viên khoá',
    'scraperPool.quotaLockedMessage': 'Scraper đã bị khoá đối với client này. Vui lòng liên hệ quản trị viên.',
    'scraperPool.quotaNoExtraRuns': 'Không còn lượt chạy thêm khả dụng trong kỳ hiện tại.',
    'scraperPool.quotaMaxUrlLimit': 'Bạn chỉ có thể thêm tối đa {limit} URL cho mỗi lượt chạy scraper.',

    'importSources.title': 'Import yourdata',
    'importSources.subtitle': 'Tải tài liệu hoặc danh sách URL Excel vào pool dữ liệu',
    'importSources.tabDocument': 'Tài liệu',
    'importSources.tabExcel': 'Danh sách URL',
    'importSources.docLabel': 'Tài liệu PDF hoặc Word',
    'importSources.docHint': 'Văn bản được trích xuất và thêm vào pool dữ liệu',
    'importSources.uploadDoc': 'Tải lên & trích xuất',
    'importSources.charsExtracted': 'Đã trích xuất {n} ký tự',
    'importSources.excelLabel': 'File Excel (.xlsx)',
    'importSources.excelHint': 'Dán URL bài viết đầy đủ (không phải trang chủ). Link có thể ở cột URL/Link hoặc bất kỳ ô nào.',
    'importSources.invalidSampleTitle': 'Lỗi validation',
    'importSources.uploadExcel': 'Tải bảng tính',
    'importSources.sheetLabel': 'Sheet',
    'importSources.selectSheet': 'Chọn sheet',
    'importSources.headerRow': 'Dòng tiêu đề',
    'importSources.preview': 'Xem trước',
    'importSources.validate': 'Kiểm tra',
    'importSources.confirmCrawl': 'Bắt đầu crawl',
    'importSources.validCount': '{n} hợp lệ',
    'importSources.invalidCount': '{n} không hợp lệ',
    'importSources.duplicateCount': '{n} trùng',
    'importSources.crawling': 'Đang crawl URL ở nền…',
    'importSources.crawlFailed': 'Crawl thất bại',
    'importSources.toastDocSuccess': 'Đã nhập tài liệu thành công',
    'importSources.toastDocFailed': 'Nhập tài liệu thất bại',
    'importSources.toastExcelUploaded': 'Đã tải bảng tính',
    'importSources.toastExcelFailed': 'Tải bảng tính thất bại',
    'importSources.toastPreviewReady': 'Sẵn sàng xem trước',
    'importSources.toastPreviewFailed': 'Xem trước thất bại',
    'importSources.toastValidated': 'Kiểm tra hoàn tất',
    'importSources.toastValidateFailed': 'Kiểm tra thất bại',
    'importSources.toastCrawlStarted': 'Đã bắt đầu crawl',
    'importSources.toastConfirmFailed': 'Không thể bắt đầu crawl',
    'importSources.toastCrawlDone': 'Crawl tất cả URL thành công',
    'importSources.toastCrawlPartial': 'Crawl xong với một số lỗi',
    'importSources.toastCrawlFailed': 'Crawl thất bại',

    'importSources.toastDocReady': 'Tài liệu sẵn sàng — hãy xác nhận để nhập',
    'importSources.confirmDoc': 'Xác nhận nhập',
    'importSources.toastDocConfirmed': 'Tài liệu đã được xác nhận',
    'importSources.toastDocConfirmFailed': 'Không thể xác nhận tài liệu nhập',

    'campaigns.strategy.notSaved': 'Chưa lưu',
    'campaigns.strategy.lastSaved': 'Lưu lần cuối {date}',
    'campaigns.strategy.tzLondon': '(UTC+00:00) London, Vương quốc Anh',
    'campaigns.strategy.tzBerlin': '(UTC+01:00) Berlin, Đức',
    'campaigns.strategy.tzHcm': '(GMT+7) TP. Hồ Chí Minh, Việt Nam',
    'campaigns.strategy.tzParis': '(UTC+01:00) Paris, Pháp',
    'campaigns.strategy.catSaas': 'SaaS / Công nghệ',
    'campaigns.strategy.catB2b': 'Marketing B2B',
    'campaigns.strategy.catFintech': 'FinTech',
    'campaigns.strategy.catHealthcare': 'Y tế',
    'campaigns.strategy.errObjectiveRequired': 'Mục tiêu là bắt buộc.',
    'campaigns.strategy.errAudienceRequired': 'Đối tượng mục tiêu là bắt buộc.',
    'campaigns.strategy.errBrandRequired': 'Hướng dẫn thương hiệu là bắt buộc.',
    'campaigns.strategy.errFrequencyRequired': 'Tần suất đăng là bắt buộc.',
    'campaigns.strategy.errMaxChars': 'Tối đa {n} ký tự.',
    'campaigns.strategy.errAddOne': 'Thêm ít nhất một {item}.',
    'campaigns.strategy.errMaxItems': 'Tối đa {n} mục {item}.',
    'campaigns.strategy.errItemMaxLen': 'Mỗi {item} tối đa {n} ký tự.',
    'campaigns.strategy.itemTone': 'giọng điệu',
    'campaigns.strategy.itemPillar': 'trụ cột',
    'campaigns.strategy.itemMessage': 'thông điệp',
    'campaigns.strategy.itemHashtag': 'hashtag',
    'campaigns.strategy.itemTime': 'thời gian',
    'campaigns.strategy.itemCompetitor': 'đối thủ',
    'campaigns.strategy.previewBrand': 'Định vị thương hiệu: {text}',
    'campaigns.strategy.previewPillars': 'Trụ cột nội dung: {text}',
    'campaigns.strategy.previewTone': 'Giọng điệu: {text}',
    'campaigns.strategy.previewMessages': 'Thông điệp chính: {text}',
    'campaigns.strategy.previewHashtags': 'Kế hoạch hashtag: {text}',

    'linkedInSchedule.title': 'Lịch LinkedIn',
    'linkedInSchedule.subtitle': 'Lên kế hoạch, sắp xếp và tự động hóa bài đăng chiến dịch.',
    'linkedInSchedule.autoSchedule': 'Tự động lên lịch',
    'linkedInSchedule.newCampaign': 'Chiến dịch mới',
    'linkedInSchedule.newPost': 'Bài đăng mới',
    'linkedInSchedule.allCampaigns': 'Tất cả chiến dịch',
    'linkedInSchedule.allStatus': 'Tất cả trạng thái',
    'linkedInSchedule.allLanguages': 'Tất cả ngôn ngữ',
    'linkedInSchedule.searchPlaceholder': 'Tìm bài đăng, tiêu đề hoặc chiến dịch…',
    'linkedInSchedule.prevDays': 'Ngày trước',
    'linkedInSchedule.nextDays': 'Ngày sau',
    'linkedInSchedule.statTotalScheduled': 'Tổng bài đã lên lịch',
    'linkedInSchedule.statDraftsPending': 'Bản nháp chờ duyệt',
    'linkedInSchedule.statActiveCampaigns': 'Chiến dịch đang chạy',
    'linkedInSchedule.statUpcomingAutoPosts': 'Bài tự động sắp tới',
    'linkedInSchedule.statPostingFrequency': 'Tần suất đăng',
    'linkedInSchedule.statGrowth': '+12% so với 7 ngày trước',
    'linkedInSchedule.statNeedsReview': 'Cần xem lại',
    'linkedInSchedule.statRunningPaused': '{active} đang chạy · {paused} tạm dừng',
    'linkedInSchedule.statNextPost': 'Tiếp theo: Hôm nay, 10:00',
    'linkedInSchedule.statFrequencyValue': '4,2 bài/ngày',
    'linkedInSchedule.statOptimal': 'Tối ưu',
    'linkedInSchedule.gridCampaigns': 'Chiến dịch',
    'linkedInSchedule.postsProgress': '{approved} / {total} bài',
    'linkedInSchedule.dragHint': 'Kéo-thả bài đăng để đổi lịch',
    'linkedInSchedule.viewMonth': 'Tháng',
    'linkedInSchedule.viewWeek': 'Tuần',
    'linkedInSchedule.viewList': 'Danh sách',
    'linkedInSchedule.draft': 'Bản nháp',
    'linkedInSchedule.postDetails': 'Chi tiết bài đăng',
    'linkedInSchedule.dayDetails': 'Chi tiết {day}',
    'linkedInSchedule.postTitle': 'Tiêu đề bài đăng',
    'linkedInSchedule.language': 'Ngôn ngữ',
    'linkedInSchedule.scheduledDateTime': 'Ngày & giờ đăng',
    'linkedInSchedule.campaign': 'Chiến dịch',
    'linkedInSchedule.objectiveCta': 'Mục tiêu / CTA',
    'linkedInSchedule.approval': 'Phê duyệt',
    'linkedInSchedule.schedule': 'Lên lịch',
    'linkedInSchedule.publishNow': 'Đăng ngay',
    'linkedInSchedule.saveDraft': 'Lưu bản nháp',
    'linkedInSchedule.deletePost': 'Xóa bài đăng',
    'linkedInSchedule.storedPosts': 'Bài đã lưu',
    'linkedInSchedule.draftEmpty': 'Kéo bài vào đây để lưu bản nháp.',
    'linkedInSchedule.dayPostsCount': '{count} bài vào {day}',
    'linkedInSchedule.noPostsDay': 'Không có bài nào được lên lịch trong ngày này.',
    'linkedInSchedule.hoverLocation': 'Vị trí',
    'linkedInSchedule.hoverRole': 'Vai trò',
    'linkedInSchedule.hoverTime': 'Giờ',
    'linkedInSchedule.hoverDate': 'Ngày',
    'linkedInSchedule.scheduleConflict': 'Xung đột lịch vào thời điểm này',
    'linkedInSchedule.linkedInAccount': 'Tài khoản LinkedIn',
    'linkedInSchedule.removeFromDraft': 'Xóa {title} khỏi bản nháp',
    'linkedInSchedule.datePickerTitle': 'Chọn khoảng thời gian',
    'linkedInSchedule.chooseStartDate': 'Chọn ngày bắt đầu',
    'linkedInSchedule.chooseEndDate': 'Chọn ngày kết thúc',
    'linkedInSchedule.closeDatePicker': 'Đóng bộ chọn ngày',
    'linkedInSchedule.prevMonth': 'Tháng trước',
    'linkedInSchedule.nextMonth': 'Tháng sau',
    'linkedInSchedule.startDate': 'Ngày bắt đầu',
    'linkedInSchedule.endDate': 'Ngày kết thúc',
    'linkedInSchedule.clearDates': 'Xóa',
    'linkedInSchedule.saveDates': 'Lưu ngày',
    'linkedInSchedule.approval.approved': 'Đã duyệt',
    'linkedInSchedule.approval.pending': 'Đang chờ',
    'linkedInSchedule.approval.rejected': 'Đã từ chối',
    'campaigns.tab.overview': 'Tổng quan',
    'campaigns.tab.content': 'Nội dung',
    'campaigns.tab.strategy': 'Chiến lược',
    'campaigns.tab.schedule': 'Lịch đăng',
    'campaigns.tab.analytics': 'Phân tích',
    'campaigns.tab.settings': 'Cài đặt',
    'campaigns.title': 'LinkedIn',
    'campaigns.newLinkedIn': 'LinkedIn mới',
    'campaigns.searchPlaceholder': 'Tìm LinkedIn…',
    'campaigns.allStatus': 'Tất cả trạng thái',
    'campaigns.campaignCount': '{count} chiến dịch',
    'campaigns.status.active': 'Đang chạy',
    'campaigns.status.completed': 'Hoàn thành',
    'campaigns.preview': 'Xem trước',
    'campaigns.edit': 'Chỉnh sửa',
    'campaigns.createdOn': 'Tạo ngày {date}',
    'campaigns.type.awareness': 'Nhận diện thương hiệu',
    'campaigns.type.productLaunch': 'Ra mắt sản phẩm',
    'campaigns.type.engagement': 'Tương tác',
    'campaigns.type.content': 'Nội dung',
    'campaigns.type.event': 'Sự kiện',
    'campaigns.type.recruitment': 'Tuyển dụng',
    'campaigns.type.thoughtLeadership': 'Tư duy dẫn dắt',
    'campaigns.contentReview': 'Duyệt nội dung',
    'campaigns.contentReviewDesc': 'Xem và phê duyệt nội dung trước khi lên lịch',
    'campaigns.language': 'Ngôn ngữ',
    'campaigns.posts': 'Bài đăng',
    'campaigns.postsInCampaign': '{count} trong chiến dịch này',
    'campaigns.addNewPost': 'Thêm bài mới',
    'campaigns.postTitle': 'Tiêu đề bài đăng',
    'campaigns.content': 'Nội dung',
    'campaigns.media': 'Phương tiện',
    'campaigns.noImages': 'Chưa có ảnh',
    'campaigns.imageCount': '{count} ảnh',
    'campaigns.uploadedMedia': 'Phương tiện đã tải lên',
    'campaigns.callToAction': 'Kêu gọi hành động',
    'campaigns.cta.learnMore': 'Tìm hiểu thêm',
    'campaigns.cta.signUp': 'Đăng ký',
    'campaigns.cta.contact': 'Liên hệ',
    'campaigns.cta.download': 'Tải xuống',
    'campaigns.saveDraft': 'Lưu bản nháp',
    'campaigns.approveSchedule': 'Duyệt & lên lịch',
    'campaigns.noPostSelected': 'Chưa chọn bài đăng',
    'campaigns.addFirstPost': 'Nhấp "Thêm bài mới" để tạo bài đăng đầu tiên',
    'campaigns.comingSoon': 'Phần này sắp ra mắt',
    'campaigns.aiStrategyAssistant': 'Trợ lý chiến lược AI',
    'campaigns.aiContentAssistant': 'Trợ lý nội dung AI',
    'campaigns.newChat': 'Chat mới',
    'campaigns.strategyOverview': 'Tổng quan chiến lược',
    'campaigns.strategyOverviewDesc': 'Xác định chiến lược nội dung cho chiến dịch LinkedIn. Chiến lược này sẽ là ngữ cảnh cho AI tạo nội dung.',
    'campaigns.fixRequired': 'Sửa các trường bắt buộc',
    'campaigns.unsavedChanges': 'Thay đổi chưa lưu',
    'campaigns.objective': 'Mục tiêu chiến dịch',
    'campaigns.targetAudience': 'Đối tượng mục tiêu',
    'campaigns.category': 'Danh mục',
    'campaigns.toneOfVoice': 'Giọng điệu',
    'campaigns.tonePlaceholder': 'Thêm giọng điệu (vd. Chuyên nghiệp)',
    'campaigns.toneNotesPlaceholder': 'Ghi chú giọng điệu (tùy chọn)…',
    'campaigns.brandGuidelines': 'Hướng dẫn thương hiệu',
    'campaigns.competitorPlaceholder': 'Thêm đối thủ',
    'campaigns.contentPillars': 'Trụ cột nội dung',
    'campaigns.addPillar': 'Thêm trụ cột',
    'campaigns.keyMessages': 'Thông điệp chính',
    'campaigns.addMessage': 'Thêm thông điệp',
    'campaigns.hashtagStrategy': 'Chiến lược hashtag',
    'campaigns.hashtagPlaceholder': 'Thêm hashtag',
    'campaigns.hashtagNotesPlaceholder': 'Ghi chú hashtag…',
    'campaigns.postingFrequency': 'Tần suất đăng',
    'campaigns.preferredPostingTime': 'Giờ đăng ưu tiên',
    'campaigns.strategyDirtyHint': 'Lưu để áp dụng chiến lược cho AI tạo từng bài.',
    'campaigns.strategySynced': 'Chiến lược đã đồng bộ với lần lưu cuối.',
    'campaigns.generating': 'Đang tạo…',
    'campaigns.generateStrategyAi': 'Tạo chiến lược bằng AI',
    'campaigns.saveStrategy': 'Lưu chiến lược',
    'campaigns.saving': 'Đang lưu…',
    'campaigns.generateStrategy': 'Tạo chiến lược',
    'campaigns.suggestPillars': 'Gợi ý trụ cột nội dung',
    'campaigns.suggestPillarsDesc': 'Gợi ý trụ cột nội dung dựa trên mục tiêu của bạn.',
    'campaigns.suggestMessages': 'Gợi ý thông điệp chính',
    'campaigns.suggestMessagesDesc': 'Tạo thông điệp phù hợp với đối tượng mục tiêu.',
    'campaigns.suggestHashtags': 'Gợi ý hashtag',
    'campaigns.suggestHashtagsDesc': 'Tìm hashtag phù hợp để tăng phạm vi tiếp cận.',
    'campaigns.suggestTimes': 'Gợi ý giờ đăng',
    'campaigns.suggestTimesDesc': 'Giờ đăng tối ưu dựa trên đối tượng của bạn.',
    'campaigns.askAnything': 'Hỏi tôi bất cứ điều gì…',
    'campaigns.addChip': 'Thêm chip',
    'campaigns.addListItem': 'Thêm mục danh sách',
    'campaigns.addItem': 'Thêm mục',
    'campaigns.toastStrategySaved': 'Đã lưu chiến lược. Bài mới sẽ dùng ngữ cảnh này cho AI.',
    'campaigns.scheduleTitle': 'Lịch LinkedIn',
    'campaigns.scheduleSubtitle': 'Kéo-thả bài đăng để lên lịch hoặc đổi lịch',
    'campaigns.prevWeek': 'Tuần trước',
    'campaigns.nextWeek': 'Tuần sau',
    'campaigns.showUnscheduled': 'Hiện bài chưa lên lịch',
    'campaigns.allDay': 'Cả ngày',
    'campaigns.conflict': 'Xung đột',
    'campaigns.postsOnCalendar': '{name} — {count} bài trên một lịch',
    'campaigns.scheduledUnscheduled': '{scheduled} đã lên lịch · {unscheduled} chưa lên lịch',
    'campaigns.draggingPost': 'Đang kéo bài…',
    'campaigns.schedulingConflicts': '{count} xung đột lịch',
    'campaigns.scheduleRules': 'Quy tắc lịch đăng',
    'campaigns.scheduleRulesDesc': 'Tối đa {maxPerDay} bài/ngày · cách tối thiểu {minHours} giờ · bài đã đăng không thể di chuyển. Nhấp bài để mở trong Nội dung.',
    'campaigns.unscheduledPosts': 'Bài chưa lên lịch',
    'campaigns.dropToUnschedule': 'Thả vào đây để bỏ lịch',
    'campaigns.noUnscheduledPosts': 'Không có bài chưa lên lịch',
    'campaigns.errPostedCannotReschedule': 'Bài đã đăng không thể đổi lịch',
    'campaigns.overview.campaignStatus': 'Trạng thái chiến dịch',
    'campaigns.overview.objective': 'Mục tiêu',
    'campaigns.overview.targetAudience': 'Đối tượng mục tiêu',
    'campaigns.overview.category': 'Danh mục',
    'campaigns.overview.owner': 'Phụ trách',
    'campaigns.overview.postStatusSummary': 'Tóm tắt trạng thái bài',
    'campaigns.overview.campaignProgress': 'Tiến độ chiến dịch',
    'campaigns.overview.schedulePreview': 'Xem trước lịch',
    'campaigns.overview.strategySnapshot': 'Ảnh chiến lược',
    'campaigns.overview.topHashtags': 'Hashtag hàng đầu',
    'campaigns.overview.analyticsSnapshot': 'Ảnh phân tích',
    'campaigns.overview.lastUpdated': 'Cập nhật lần cuối: {date}',
    'campaigns.overview.recentActivity': 'Hoạt động gần đây',
    'campaigns.overview.viewProgress': 'Xem chi tiết tiến độ +',
    'campaigns.overview.viewSchedule': 'Xem toàn bộ lịch +',
    'campaigns.overview.viewStrategy': 'Xem chiến lược +',
    'campaigns.overview.viewAnalytics': 'Xem phân tích +',
    'campaigns.overview.viewActivity': 'Xem tất cả hoạt động +',
    'campaigns.overview.openSchedule': 'Mở toàn bộ lịch',
    'campaigns.overview.impressions': 'Lượt hiển thị',
    'campaigns.overview.clicks': 'Lượt nhấp',
    'campaigns.overview.reactions': 'Phản ứng',
    'campaigns.overview.comments': 'Bình luận',
    'campaigns.overview.shares': 'Chia sẻ',
    'campaigns.overview.engagementRate': 'Tỷ lệ tương tác',
    'campaigns.overview.archived': 'Đã lưu trữ',
    'campaigns.overview.nextPost': 'Bài tiếp theo',
    'campaigns.overview.following': 'Tiếp theo',
    'campaigns.overview.viewAllPosts': 'Xem tất cả bài +',
    'campaigns.overview.totalPosts': 'tổng bài',
    'campaigns.overview.completionRate': 'Tỷ lệ hoàn thành',
    'campaigns.overview.totalPostsLabel': 'Tổng bài',
    'campaigns.overview.members': '{count} thành viên',
    'campaigns.overview.moreHashtags': '+{count} nữa',
    'campaigns.overview.daysRemaining': 'Còn {count} ngày',
    'campaigns.overview.activity.postApproved': 'Bài {post} đã được duyệt',
    'campaigns.overview.activity.postScheduled': 'Bài {post} đã được lên lịch',
    'campaigns.overview.activity.postPublished': 'Bài {post} đã được đăng',
    'campaigns.overview.activity.aiApplied': 'Gợi ý nội dung AI đã áp dụng cho Bài {post}',
    'campaigns.overview.activity.settingsUpdated': 'Cài đặt chiến dịch đã được cập nhật',
    'campaigns.settings.general': 'Cài đặt chung',
    'campaigns.settings.generalDesc': 'Quản lý thông tin cơ bản và tùy chọn mặc định cho chiến dịch này.',
    'campaigns.settings.campaignName': 'Tên chiến dịch',
    'campaigns.settings.defaultLanguage': 'Ngôn ngữ mặc định',
    'campaigns.settings.timeZone': 'Múi giờ',
    'campaigns.settings.approval': 'Quy trình phê duyệt',
    'campaigns.settings.approvalDesc': 'Cấu hình ai có thể xem và phê duyệt nội dung trước khi đăng.',
    'campaigns.settings.reviewers': 'Người xem xét',
    'campaigns.settings.approvers': 'Người duyệt',
    'campaigns.settings.autoApproveDrafts': 'Tự duyệt bản nháp',
    'campaigns.settings.reminder': 'Nhắc nhở',
    'campaigns.settings.approvalNote': 'Nội dung nháp sẽ cần phê duyệt thủ công.',
    'campaigns.settings.notifications': 'Thông báo',
    'campaigns.settings.notificationsDesc': 'Chọn cập nhật bạn muốn nhận.',
    'campaigns.settings.notifyApproval': 'Yêu cầu phê duyệt nội dung',
    'campaigns.settings.notifyReminders': 'Nhắc bài đã lên lịch',
    'campaigns.settings.notifyPerformance': 'Cảnh báo hiệu suất bài đăng',
    'campaigns.settings.notifyStatus': 'Thay đổi trạng thái chiến dịch',
    'campaigns.settings.integrations': 'Tích hợp',
    'campaigns.settings.integrationsDesc': 'Kết nối công cụ và dịch vụ bên ngoài.',
    'campaigns.settings.connected': 'Đã kết nối',
    'campaigns.settings.manage': 'Quản lý',
    'campaigns.settings.privacy': 'Dữ liệu & quyền riêng tư',
    'campaigns.settings.privacyDesc': 'Quản lý lưu trữ dữ liệu và tùy chọn quyền riêng tư.',
    'campaigns.settings.dataRetention': 'Lưu trữ dữ liệu',
    'campaigns.settings.anonymize': 'Ẩn danh dữ liệu phân tích',
    'campaigns.settings.anonymizeDesc': 'Loại bỏ thông tin nhận dạng cá nhân.',
    'campaigns.settings.dangerZone': 'Vùng nguy hiểm',
    'campaigns.settings.dangerDesc': 'Hành động không thể hoàn tác và nhạy cảm.',
    'campaigns.settings.archiveCampaign': 'Lưu trữ chiến dịch',
    'campaigns.settings.archiveDesc': 'Lưu trữ chiến dịch và toàn bộ nội dung liên quan.',
    'campaigns.settings.archive': 'Lưu trữ',
    'campaigns.settings.deleteCampaign': 'Xóa chiến dịch',
    'campaigns.settings.deleteDesc': 'Không thể hoàn tác. Mọi dữ liệu sẽ bị xóa vĩnh viễn.',
    'campaigns.settings.saveChanges': 'Lưu thay đổi',
    'campaigns.settings.saving': 'Đang lưu…',
    'campaigns.datePicker.chooseStart': 'Chọn ngày bắt đầu',
    'campaigns.datePicker.chooseEnd': 'Chọn ngày kết thúc',
    'campaigns.datePicker.reference': 'Tham chiếu từ Lịch LinkedIn',
    'campaigns.datePicker.close': 'Đóng bộ chọn ngày',
    'campaigns.datePicker.prevMonth': 'Tháng trước',
    'campaigns.datePicker.nextMonth': 'Tháng sau',

    'linkedInSchedule.status.scheduled': 'Đã lên lịch',
    'linkedInSchedule.status.draft': 'Bản nháp',
    'linkedInSchedule.status.approved': 'Đã duyệt',
    'linkedInSchedule.status.posted': 'Đã đăng',

    'dataroom.selectedArticles': 'Bài đã chọn ({count})',
    'dataroom.selectionHint': 'Nhấp bài viết ở DataRoom để chọn, hoặc kéo-thả vào vùng này.',
    'dataroom.noArticlesSelected': 'Chưa có bài viết nào được chọn.',
    'dataroom.deselectArticle': 'Bỏ chọn bài viết',
    'dataroom.breadcrumbDataRooms': 'DataRooms',
    'dataroom.pageTitle': 'Cài đặt Dataroom',
    'dataroom.libraryAriaLabel': 'Bài viết Dataroom',
    'dataroom.deliveryPublishingTitle': 'Gửi & Xuất bản',
    'dataroom.deliveryPublishingSubtitle': 'Cấu hình cách digest DataRoom này được tạo, xem lại và gửi đi.',
    'dataroom.dataroomTitle': 'DataRoom',
    'dataroom.savedArticles': '{count} bài đã lưu',
    'dataroom.savedArticlesPlural': '{count} bài đã lưu',
    'dataroom.searchDataroom': 'Tìm trong DataRoom...',
    'dataroom.searchNoResults': 'Không có bài phù hợp tìm kiếm.',
    'dataroom.collectedTopicsAria': 'Chủ đề đã thu thập',
    'dataroom.noTopicsYet': 'Chưa có chủ đề',
    'dataroom.collectedArticlesEmpty': 'Bài đã thu thập sẽ hiển thị ở đây.',
    'dataroom.removeArticleFromDataroom': 'Gỡ {title} khỏi DataRoom',
    'dataroom.general': 'Chung',
    'dataroom.sendNow': 'Gửi ngay',
    'dataroom.scheduleSend': 'Lên lịch gửi',
    'dataroom.exportFile': 'Xuất tệp',
    'dataroom.sendNowDesc': 'Tạo và gửi digest ngay lập tức.',
    'dataroom.scheduleSendDesc': 'Gửi một lần hoặc lặp lại vào thời điểm đã cấu hình.',
    'dataroom.exportFileDesc': 'Tải digest dưới dạng PDF, DOCX hoặc CSV.',
    'dataroom.deliveryMethodAria': 'Phương thức gửi',
    'dataroom.saveDraft': 'Lưu bản nháp',
    'dataroom.sendEmailNow': 'Gửi email ngay',
    'dataroom.downloadFile': 'Tải tệp',
    'dataroom.working': 'Đang xử lý…',
    'dataroom.configuration': 'Cấu hình',
    'dataroom.recipients': 'Người nhận',
    'dataroom.recipientsSelected': 'Đã chọn {count} người nhận',
    'dataroom.recipientsSelectedPlural': 'Đã chọn {count} người nhận',
    'dataroom.searchSubscriberPlaceholder': 'Tìm người đăng ký theo tên hoặc email…',
    'dataroom.searchByNameOrEmail': 'Tìm theo tên hoặc email…',
    'dataroom.selectAll': 'Chọn tất cả',
    'dataroom.clear': 'Xóa',
    'dataroom.loadingSubscribers': 'Đang tải người đăng ký…',
    'dataroom.noSubscribersFound': 'Không tìm thấy người đăng ký.',
    'dataroom.selectedRecipients': 'Người nhận đã chọn ({count})',
    'dataroom.removeRecipient': 'Gỡ {email}',
    'dataroom.emailSubject': 'Tiêu đề email',
    'dataroom.sendNowInfo': 'Digest này sẽ được gửi ngay sau khi bạn xem trước và nhấp Gửi email ngay.',
    'dataroom.scheduleType': 'Loại lịch',
    'dataroom.oneTime': 'Một lần',
    'dataroom.recurring': 'Lặp lại',
    'dataroom.date': 'Ngày',
    'dataroom.time': 'Giờ',
    'dataroom.timezone': 'Múi giờ',
    'dataroom.recurringInfo': 'Lịch lặp lại chạy hằng ngày vào giờ đã chọn (job lặp backend).',
    'dataroom.exportFormat': 'Định dạng xuất',
    'dataroom.fileContent': 'Nội dung tệp',
    'dataroom.includeArticleSummary': 'Bao gồm tóm tắt bài viết',
    'dataroom.includeSourceLinks': 'Bao gồm liên kết nguồn',
    'dataroom.includeTopicGrouping': 'Bao gồm nhóm chủ đề',
    'dataroom.includeRawUrlsOnly': 'Chỉ bao gồm URL bài viết thô',
    'dataroom.fileName': 'Tên tệp',
    'dataroom.previewValidation': 'Xem trước & Xác thực',
    'dataroom.generate': 'Tạo',
    'dataroom.generating': 'Đang tạo…',
    'dataroom.previewGenerated': 'Đã tạo xem trước',
    'dataroom.regenerate': 'Tạo lại',
    'dataroom.regenerating': 'Đang tạo lại…',
    'dataroom.generatingPreviewAi': 'Đang tạo xem trước bằng AI…',
    'dataroom.regeneratingPreview': 'Đang tạo lại xem trước…',
    'dataroom.generatedAt': 'Tạo lúc',
    'dataroom.articlesIncluded': 'Bài viết đã gồm',
    'dataroom.articlesCount': '{count} bài',
    'dataroom.topics': 'Chủ đề',
    'dataroom.recipientsCount': '{count} người nhận',
    'dataroom.reviewBeforeSend': 'Vui lòng xem trước trước khi gửi.',
    'dataroom.subject': 'Tiêu đề',
    'dataroom.openFullPreview': 'Mở xem trước đầy đủ',
    'dataroom.fullPreviewAria': 'Xem trước đầy đủ',
    'dataroom.previewDeviceSizeAria': 'Kích thước thiết bị xem trước',
    'dataroom.fullDigestPreview': 'Xem trước digest đầy đủ - {device}',
    'dataroom.selectDate': 'Chọn ngày',
    'dataroom.selectTime': 'Chọn giờ',
    'dataroom.previousMonth': 'Tháng trước',
    'dataroom.nextMonth': 'Tháng sau',
    'dataroom.weekdaySu': 'CN',
    'dataroom.weekdayMo': 'T2',
    'dataroom.weekdayTu': 'T3',
    'dataroom.weekdayWe': 'T4',
    'dataroom.weekdayTh': 'T5',
    'dataroom.weekdayFr': 'T6',
    'dataroom.weekdaySa': 'T7',
    'dataroom.hour': 'Giờ',
    'dataroom.minute': 'Phút',
    'dataroom.now': 'Bây giờ',
    'dataroom.am': 'SA',
    'dataroom.pm': 'CH',
    'dataroom.deviceIphone': 'iPhone',
    'dataroom.deviceAndroid': 'Android',
    'dataroom.devicePc': 'PC',
    'dataroom.assistantAriaLabel': 'Trợ lý nội dung AI',
    'dataroom.aiContentAssistant': 'Trợ lý nội dung AI',
    'dataroom.newChat': 'Chat mới',
    'dataroom.thinking': 'Đang suy nghĩ…',
    'dataroom.messagePlaceholder': 'Nhập tin nhắn...',
    'dataroom.messageAriaLabel': 'Tin nhắn trợ lý AI',
    'dataroom.sendMessageAria': 'Gửi tin nhắn',
    'dataroom.assistantWelcome': 'Xin chào! Kéo bài từ DataRoom vào khung chat để import làm tài liệu, rồi hỏi về nội dung. Bạn cũng có thể hỏi về thiết lập gửi hoặc xem trước.',
    'dataroom.assistantRequestFailed': 'Yêu cầu trợ lý thất bại. Vui lòng thử lại.',
    'dataroom.assistantNoArticles': 'Thêm ít nhất một bài vào DataRoom trước khi hỏi trợ lý.',
    'dataroom.assistantDropHint': 'Kéo một bài DataRoom vào đây để import vào chat.',
    'dataroom.assistantAttachedAria': 'Bài đã import vào chat này',
    'dataroom.assistantImporting': 'Đang import bài…',
    'dataroom.assistantImportedArticle': 'Đã import “{title}” ({chunks} chunk). Hãy hỏi tôi về bài này.',
    'dataroom.assistantImportSuccess': 'Đã import “{title}” vào chat.',
    'dataroom.assistantImportFailed': 'Không import được bài vào chat.',
    'dataroom.assistantAlreadyImported': '“{title}” đã được import trong chat này.',
    'dataroom.assistantDropUnknownArticle': 'Bài đó không còn trong DataRoom.',
    'dataroom.validationNoArticles': 'Thêm ít nhất một bài vào DataRoom trước khi tạo xem trước.',
    'dataroom.validationNoSelected': 'Chọn hoặc kéo ít nhất một bài vào Xem trước & Xác thực.',
    'dataroom.validationGeneratePreview': 'Tạo xem trước trước khi gửi, lên lịch hoặc xuất.',
    'dataroom.validationScheduleDateTime': 'Chọn ngày và giờ trước khi lên lịch.',
    'dataroom.defaultEmailSubject': 'Digest HAWK hằng ngày - {date}',
    'dataroom.toastSelectRecipient': 'Chọn ít nhất một người nhận.',
    'dataroom.toastSelectArticle': 'Chọn ít nhất một bài để gửi.',
    'dataroom.toastGeneratePreview': 'Tạo xem trước trước khi gửi.',
    'dataroom.toastGenerateBeforeSchedule': 'Tạo xem trước trước khi lên lịch.',
    'dataroom.toastApiKeyMissing': 'Thiếu khóa API moderator. Đăng xuất và đăng nhập lại với tư cách moderator.',
    'dataroom.toastStaleArticlesRemoved': 'Đã gỡ {count} bài lỗi thời khỏi DataRoom ({titles}). Thêm lại từ Kho dữ liệu.',
    'dataroom.toastSentSuccess': 'Đã gửi tới {count} người đăng ký',
    'dataroom.toastSentSuccessPlural': 'Đã gửi tới {count} người đăng ký',
    'dataroom.toastSentPartial': 'Đã gửi {success}, thất bại {failed}. {error}',
    'dataroom.toastSendFailed': 'Gửi thất bại. Vui lòng thử lại.',
    'dataroom.toastChooseSchedule': 'Chọn ngày và giờ lịch.',
    'dataroom.toastScheduledSuccess': 'Đã lên lịch cho {count} người đăng ký',
    'dataroom.toastScheduledSuccessPlural': 'Đã lên lịch cho {count} người đăng ký',
    'dataroom.toastScheduledPartial': 'Đã lên lịch {success}, thất bại {failed}. {error}',
    'dataroom.toastScheduleFailed': 'Lên lịch thất bại. Vui lòng thử lại.',
    'dataroom.toastCsvDownloaded': 'Đã tải tệp CSV.',
    'dataroom.toastPdfPrint': 'Đã mở hộp thoại in cho xuất PDF.',
    'dataroom.toastDocDownloaded': 'Đã tải tài liệu (.doc dựa trên HTML).',
    'dataroom.toastExportFailed': 'Xuất thất bại. Vui lòng thử lại.',
    'dataroom.toastDraftSaved': 'Đã lưu cài đặt gửi dưới dạng bản nháp.',
    'dataroom.toastDraftSaveFailed': 'Không lưu được bản nháp.',
    'dataroom.errGenerationFailed': 'Tạo thất bại. Vui lòng thử lại.',
    'dataroom.errStaleArticles': '{count} bài không còn trên máy chủ ({titles}). Đã gỡ — thêm lại từ Kho dữ liệu, rồi tạo lại xem trước.',
    'dataroom.errNoValidArticles': 'Không có bài hợp lệ để gửi. Thêm lại bài từ Kho dữ liệu.',
    'dataroom.errArticleNotAnalyzed': '"{title}" chưa được phân tích. Chạy phân tích trong Kho dữ liệu trước khi gửi.',
    'dataroom.errSyncArticle': 'Không đồng bộ được "{title}" (ID {id}): {reason}',
    'dataroom.errDispatchNoHistory': 'Gửi không trả về ID lịch sử.',
    'dataroom.errEmailDeliveryFailed': 'Gửi email thất bại trên máy chủ (SMTP).',
    'dataroom.errDispatchStatus': 'Gửi hoàn tất với trạng thái "{status}".',
  },

  fr: MODERATOR_PAGE_MESSAGES_FR,
};

